import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP, fmtDateTime, fmtDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import UploadQRDialog from '../components/UploadQRDialog';
import ReceiptUploadInline from '../components/ReceiptUploadInline';
import VerificationBadge from '../components/VerificationBadge';
import VerifyPinDialog from '../components/VerifyPinDialog';
import ViewQRDialog from '../components/ViewQRDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Plus, Trash2, Send, CheckCircle2, Search, RefreshCw, Settings2,
  AlertTriangle, ChevronDown, ChevronUp, Building2, Package, X,
  TrendingUp, TrendingDown, Clock, ArrowRight, Eye, XCircle, Pencil, Upload, Check,
  ClipboardCheck, FileText, Smartphone, Lock, Pause, FolderOpen, Trash, Tag, Shield, ShieldCheck, Printer
} from 'lucide-react';
import { localTodayStr } from '../lib/dateFormat';
import PrintEngine from '../lib/PrintEngine';
import { toast } from 'sonner';
import { useUnsavedChangesGuard } from '../lib/useUnsavedChangesGuard';
import CalcInput from '../components/CalcInput';
import SmartProductSearch from '../components/SmartProductSearch';
import { getProducts as getOfflineProducts } from '../lib/offlineDB';
import ConfirmQuantitiesDialog from '../components/ConfirmQuantitiesDialog';
import RequestQRDialog from '../components/RequestQRDialog';
import SendToPrintModal from '../components/SendToPrintModal';
import StockMovementSwitcher from '../components/StockMovementSwitcher';
import { WaitChip, WhoseMoveLine, TransferContents, TransferTimeline } from '../components/TransferCardParts';
import { buildTimeline, whoseMove, sortTransfers } from '../lib/transferCard';

// Smart quantity formatter — display max 3 decimal places, no trailing zeros.
// Raw precision is preserved in the database — this is display only.
// Examples: 5.999999997 → "6", 1.333333 → "1.333", 6.0 → "6", 5.25 → "5.25"
const fmtQty = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '0';
  return parseFloat(n.toFixed(3)).toString();
};

// Prevent accidental wheel-scroll changes on number inputs.
// (User reported their value changing when they hovered + scrolled the page.)
const noWheel = (e) => { e.currentTarget.blur(); };

// Mirrors BT_VARIANCE_PIN_THRESHOLD in backend/routes/branch_transfers.py.
// A HINT only — the backend stays the authority on when a PIN is demanded.
const BT_VARIANCE_PIN_THRESHOLD = 5000;

// Book value of a receipt variance. Excess is NOT a loss, but it is value
// appearing from nowhere — it counts toward the PIN threshold (BT-GUARD-01),
// so the dialogs must show it rather than a misleading "loss ₱0.00".
// Each kind is valued twice (owner 2026-09-19): at CAPITAL (what the books
// carry — the PIN threshold is judged on this) and at RETAIL (what it would
// have sold for). atRisk stays capital-only to match the backend gate.
const varianceTotals = (order) => {
  const sum = (rows, key) => (rows || []).reduce((s, v) => s + (v[key] || 0), 0);
  const shortage = sum(order?.shortages, 'capital_variance');
  const excess = sum(order?.excesses, 'capital_variance');
  return {
    shortage, excess, atRisk: shortage + excess,
    shortageRetail: sum(order?.shortages, 'retail_variance'),
    excessRetail: sum(order?.excesses, 'retail_variance'),
  };
};

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-700',
  returned: 'bg-rose-100 text-rose-700',
  sent: 'bg-blue-100 text-blue-700',
  sent_to_terminal: 'bg-amber-100 text-amber-700',
  received_pending: 'bg-yellow-100 text-yellow-700',
  received: 'bg-emerald-100 text-emerald-700',
  disputed: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-600',
};

// Phase 1.1 — Derive a tiny per-request approval summary purely from
// the existing `items[]` already returned by `/purchase-orders` and
// `/purchase-orders/incoming-requests`. No backend change.
//   full      = approved_qty == quantity
//   reduced   = 0 < approved_qty < quantity
//   declined  = approved_qty == 0 (but explicitly set)
//   excess    = approved_qty > quantity
//   pending   = approved_qty missing / not yet confirmed
// Returns null when ALL lines are pending (nothing meaningful to show).
function deriveApprovalSummary(req) {
  const items = (req && req.items) || [];
  if (!items.length) return null;
  let full = 0, reduced = 0, declined = 0, excess = 0, pending = 0;
  for (const it of items) {
    const r = parseFloat(it.quantity || 0);
    const raw = it.approved_qty;
    const hasAppr = raw !== null && raw !== undefined && raw !== '';
    if (!hasAppr) { pending += 1; continue; }
    const a = parseFloat(raw || 0);
    if (a === 0) declined += 1;
    else if (a < r) reduced += 1;
    else if (a > r) excess += 1;
    else full += 1;
  }
  if (pending === items.length) return null;
  return { full, reduced, declined, excess, pending, total: items.length };
}

function ApprovalSummaryChips({ summary, testidPrefix = 'approval-summary' }) {
  if (!summary) return null;
  const parts = [];
  if (summary.full)     parts.push({ k: 'full',     label: `${summary.full} full`,         cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' });
  if (summary.reduced)  parts.push({ k: 'reduced',  label: `${summary.reduced} reduced`,   cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' });
  if (summary.excess)   parts.push({ k: 'excess',   label: `${summary.excess} increased`,  cls: 'bg-amber-50 text-amber-700 border-amber-200' });
  if (summary.declined) parts.push({ k: 'declined', label: `${summary.declined} declined`, cls: 'bg-rose-50 text-rose-700 border-rose-200' });
  if (summary.pending)  parts.push({ k: 'pending',  label: `${summary.pending} pending`,   cls: 'bg-slate-50 text-slate-600 border-slate-200' });
  return (
    <span className="text-[11px] text-slate-500 flex items-center gap-1 flex-wrap" data-testid={testidPrefix}>
      <span className="font-semibold text-slate-600">Request Review:</span>
      {parts.map((p) => (
        <span key={p.k}
          className={`px-2 py-0.5 rounded border ${p.cls}`}
          data-testid={`${testidPrefix}-${p.k}`}>
          {p.label}
        </span>
      ))}
    </span>
  );
}

function newRow() {
  return {
    id: Math.random().toString(36).slice(2),
    productSearch: '', productMatches: [], product: null,
    activeSearchIndex: -1,
    qty: 1,
    requested_qty: null,   // set when generated from stock request
    available_stock: null,  // set when generated from stock request
    branch_capital: 0,
    global_cost_price: 0,
    is_branch_specific_cost: false,
    transfer_capital: '',
    branch_retail: '',
    last_purchase_ref: null, moving_average_ref: null, last_branch_retail: null,
    // Target-branch snapshot (filled by selectProduct)
    target_branch_capital: 0, target_branch_moving_average: 0,
    target_branch_retail: 0, target_branch_stock: 0,
    override: false, override_reason: '',
  };
}

const MARKUP_TYPES = [
  { value: 'fixed', label: '+ Fixed ₱' },
  { value: 'percent', label: '+ % of Capital' },
];

// ── Per-row validation ──────────────────────────────────────────────────────
function validateRow(row, minMargin) {
  const tc = parseFloat(row.transfer_capital) || 0;
  const br = parseFloat(row.branch_retail) || 0;
  if (!tc && !br) return { ok: false, reason: 'incomplete', margin: 0 };
  if (!tc) return { ok: false, reason: 'no_capital', margin: 0 };
  if (!br) return { ok: false, reason: 'retail', margin: 0 };
  const margin = br - tc;
  if (tc > br) return { ok: false, reason: 'below_cost', margin };
  if (margin < minMargin) return { ok: false, reason: 'low_margin', margin };
  return { ok: true, reason: '', margin };
}

// ── Anchor-positioned dropdown (mirrors SmartProductSearch positioning) ───────
// Re-positions on scroll/resize, auto-flips up when there's no room below,
// clamps max-height to available viewport space. Used for both the main
// transfer product search and the stock-request product search.
function AnchoredDropdown({ anchor, open, children, minWidth = 320 }) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 320, flipUp: false });

  useEffect(() => {
    if (!open || !anchor) return;
    const compute = () => {
      const rect = anchor.getBoundingClientRect();
      const vh = window.innerHeight;
      const spaceBelow = vh - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const flipUp = spaceBelow < 220 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(160, Math.min(360, flipUp ? spaceAbove : spaceBelow));
      setPos({
        top: flipUp ? null : rect.bottom + 4,
        bottom: flipUp ? vh - rect.top + 4 : null,
        left: rect.left,
        width: Math.max(rect.width, minWidth),
        maxHeight,
        flipUp,
      });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open, anchor, minWidth]);

  if (!open || !anchor) return null;
  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: pos.top != null ? pos.top : undefined,
        bottom: pos.bottom != null ? pos.bottom : undefined,
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight,
        zIndex: 9999,
        // Always-visible scrollbar so the user can see their scroll position.
        // The global 6px scrollbar with transparent track was too subtle here.
        scrollbarColor: '#94a3b8 #f1f5f9',
        scrollbarWidth: 'auto',
      }}
      className="bg-white border border-slate-200 rounded-lg shadow-xl overflow-y-auto flex flex-col anchored-dropdown-scroll"
    >
      {children}
    </div>,
    document.body
  );
}

export default function BranchTransferPage() {
  const { branches, currentBranch, user, canViewAllBranches, selectedBranchId, isConsolidatedView } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const searchTimers = useRef({});
  const dropdownRefs = useRef({});
  const reqQtyRefs = useRef({});  // per-row qty input refs (Detailed-Sales-style focus handoff)
  const qtyRefs = useRef({});     // main encoder grid qty refs (audit BT-FLOW-1: sales-parity focus handoff)
  const skipResetRef = useRef(false); // skip row/branch reset when loading from request
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Lists / history ────────────────────────────────────────────────────────
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'new');
  const [historyTab, setHistoryTab] = useState(() => searchParams.get('subtab') || 'all');
  const [stockRequests, setStockRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [generatingTransfer, setGeneratingTransfer] = useState(null); // request id being processed
  // Phase 1 — "Confirm Quantities" modal state (incoming-request card).
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmTargetRequest, setConfirmTargetRequest] = useState(null);
  // Phase 2.1 — "View QR" modal state (incoming + outgoing cards).
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrTargetRequest, setQrTargetRequest] = useState(null);
  // ── Request Stock form state ────────────────────────────────────────────
  const [reqTargetBranch, setReqTargetBranch] = useState('');
  const [reqRows, setReqRows] = useState([{ id: Date.now(), product: null, qty: '' }]);

  // Unsaved-changes guard — fires when the request form has at least one
  // chosen product. Provider renders the dialog.
  useUnsavedChangesGuard({
    isDirty: (reqRows || []).some(r => r.product),
    label: 'Branch Transfer Request',
  });
  const [reqNotes, setReqNotes] = useState('');
  const [reqSaving, setReqSaving] = useState(false);
  const [reqShowRetail, setReqShowRetail] = useState(true);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [outgoingLoading, setOutgoingLoading] = useState(false);
  const [requestsView, setRequestsView] = useState('incoming'); // 'incoming' | 'outgoing'

  // Sync tab state from URL params (for deep-linking from notifications)
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const urlSubtab = searchParams.get('subtab');
    // P7: the legacy request form is retired — old deep links follow the
    // signpost to the one front door.
    if (urlTab === 'request') { navigate('/stock-requests'); return; }
    if (urlTab && urlTab !== tab) setTab(urlTab);
    if (urlSubtab && urlSubtab !== historyTab) {
      // Map legacy subtab values to new status-based keys
      const subtabMap = { outgoing: 'all', incoming: 'all' };
      setHistoryTab(subtabMap[urlSubtab] || urlSubtab);
    }
    if (urlTab || urlSubtab) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [receiveDialog, setReceiveDialog] = useState(false);
  const [receiveSaving, setReceiveSaving] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null); // ID of draft being edited
  const [requestContext, setRequestContext] = useState(null); // { po_id, po_number } when generated from stock request

  // Mark-as-complete dialog state (for partially_fulfilled requests)
  const [closePartialDialog, setClosePartialDialog] = useState(false);
  const [closePartialTarget, setClosePartialTarget] = useState(null); // the PO object
  const [closePartialReason, setClosePartialReason] = useState('');
  const [closePartialSaving, setClosePartialSaving] = useState(false);

  // Smart Capital Dialog for branch transfers
  const [transferCapitalDialog, setTransferCapitalDialog] = useState(false);
  const [transferCapitalPreview, setTransferCapitalPreview] = useState(null);
  const [transferCapitalChoices, setTransferCapitalChoices] = useState({});
  const [transferCapitalPendingItems, setTransferCapitalPendingItems] = useState(null);
  // WS2 — the receiver's answer to "what's your retail?" per product_id.
  const [transferRetailChoices, setTransferRetailChoices] = useState({});

  // ── New transfer form ──────────────────────────────────────────────────────
  const [fromBranchId, setFromBranchId] = useState(() => currentBranch?.id || '');
  const [toBranchId, setToBranchId] = useState('');
  const [minMargin, setMinMargin] = useState(20);
  const [categoryMarkups, setCategoryMarkups] = useState([]);
  const [markupPanelOpen, setMarkupPanelOpen] = useState(false);
  const [rows, setRows] = useState(() => [newRow()]);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [templateLoaded, setTemplateLoaded] = useState(false);

  // Park / Resume — draft transfer staging
  const [parkedTransfers, setParkedTransfers] = useState([]);
  const [parkDialogOpen, setParkDialogOpen] = useState(false);
  const [parkingNow, setParkingNow] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [parkLabel, setParkLabel] = useState('');
  const [activeParkId, setActiveParkId] = useState(null); // ID of currently-resumed park, if any

  // Sync fromBranchId with currentBranch on initial load
  useEffect(() => {
    if (currentBranch?.id && !fromBranchId) setFromBranchId(currentBranch.id);
  }, [currentBranch?.id]); // eslint-disable-line

  const destBranch = branches.find(b => b.id === toBranchId);

  // Load categories once
  useEffect(() => {
    api.get('/products/categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  // Reset rows and template when source branch changes
  useEffect(() => {
    if (skipResetRef.current) return;
    setRows([newRow()]);
    setToBranchId('');
    setTemplateLoaded(false);
  }, [fromBranchId]); // eslint-disable-line

  // Load markup template when destination branch changes
  useEffect(() => {
    if (!toBranchId) { setTemplateLoaded(false); return; }
    if (skipResetRef.current) { skipResetRef.current = false; setTemplateLoaded(true); return; }
    setRows([newRow()]); // Reset rows for new destination
    api.get(`/branch-transfers/markup-template/${toBranchId}`)
      .then(r => {
        setMinMargin(r.data.min_margin ?? 20);
        setCategoryMarkups(r.data.category_markups || []);
        setTemplateLoaded(true);
      })
      .catch(() => setTemplateLoaded(true));
  }, [toBranchId]);

  // Re-apply markups to all rows when categoryMarkups change
  const skipMarkupReapplyRef = useRef(false); // set by the Edit hydration, which already applied them
  useEffect(() => {
    if (skipMarkupReapplyRef.current) { skipMarkupReapplyRef.current = false; return; }
    if (!rows.length) return;
    setRows(prev => prev.map(row => applyMarkupToRow(row, categoryMarkups)));
  }, [categoryMarkups]); // eslint-disable-line

  // Load transfer history — filtered by branch context
  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const params = { limit: 100 };
      // Admin in specific branch view: filter by that branch
      if (canViewAllBranches && !isConsolidatedView && currentBranch?.id) {
        params.branch_id = currentBranch.id;
      }
      // isConsolidatedView (All Branches) → no filter, admin sees everything
      const res = await api.get('/branch-transfers', { params });
      setOrders(res.data.orders || []);
    } catch { toast.error('Failed to load transfer history'); }
    setOrdersLoading(false);
  }, [canViewAllBranches, isConsolidatedView, currentBranch?.id]);

  useEffect(() => { if (tab === 'history') loadOrders(); }, [tab, loadOrders]);

  // Audit D11: ProductDetailPage emits /branch-transfers?ref=<product_id>
  // ("start a transfer for this product") but nothing ever consumed it —
  // the link landed on a blank New Transfer tab. Prefill the first row's
  // product search with the product's name so the encoder picks it up.
  useEffect(() => {
    const refPid = searchParams.get('ref');
    if (!refPid) return;
    api.get(`/products/${refPid}`).then(res => {
      const name = res.data?.name || res.data?.product?.name;
      if (name) {
        setTab('new');
        setRows(rs => {
          if (!rs.length) return rs;
          const next = [...rs];
          next[0] = { ...next[0], productSearch: name };
          return next;
        });
        setTimeout(() => {
          const el = document.querySelector('[data-testid^="product-search-"]');
          if (el) { el.focus(); el.dispatchEvent(new Event('input', { bubbles: true })); }
        }, 400);
      }
    }).catch(() => {});
    searchParams.delete('ref');
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line

  // Auto-open view dialog when ?view=TRANSFER_ID is in URL (from Audit Center etc.)
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && !viewOrder) {
      setTab('history');
      // Fetch the specific transfer and open view dialog
      api.get(`/branch-transfers/${viewId}`).then(res => {
        setViewOrder(res.data);
        searchParams.delete('view');
        setSearchParams(searchParams, { replace: true });
      }).catch(() => {});
    }
  }, [searchParams]); // eslint-disable-line

  // Load incoming stock requests (branch request POs directed at this branch)
  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const effectiveBranch = currentBranch?.id || user?.branch_id || '';
      const params = effectiveBranch ? { branch_id: effectiveBranch } : {};
      const res = await api.get('/purchase-orders/incoming-requests', { params });
      setStockRequests(res.data.requests || []);
    } catch { }
    setRequestsLoading(false);
  }, [currentBranch?.id, user?.branch_id]);

  useEffect(() => { if (tab === 'history') loadRequests(); }, [tab, loadRequests]);

  // Generate a branch transfer from a stock request.
  // Phase 0.5 — branch-context aware:
  //   * Non-admin: hidden in UI when currentBranch != supply_branch_id.
  //     If the button somehow reaches this handler from the wrong
  //     context (deep link, stale state), the backend will 403.
  //   * Admin: button shown, but if currentBranch differs from the
  //     supply branch we prompt before sending the request.
  const handleGenerateTransfer = async (request) => {
    const myBranchId = currentBranch?.id || user?.branch_id || '';
    const supplyBranchId = request.supply_branch_id || '';
    if (isAdmin && supplyBranchId && myBranchId && supplyBranchId !== myBranchId) {
      const supplyName = (branches || []).find(b => b.id === supplyBranchId)?.name || supplyBranchId;
      const myName = currentBranch?.name || 'current branch';
      const ok = window.confirm(
        `You are viewing as "${myName}". This will prepare a transfer FROM "${supplyName}".\n\nContinue anyway?`
      );
      if (!ok) return;
    }
    setGeneratingTransfer(request.id);
    try {
      const res = await api.post(`/purchase-orders/${request.id}/generate-branch-transfer`);
      const transferData = res.data;
      // Store request context for linking
      setRequestContext({ po_id: transferData.po_id, po_number: transferData.po_number });
      // Skip reset effects when pre-filling from request
      skipResetRef.current = true;
      // Pre-load the New Transfer form with the data
      setFromBranchId(transferData.from_branch_id || '');
      setToBranchId(transferData.to_branch_id || '');
      setMinMargin(20);
      const editRows = transferData.items.map(item => ({
        ...newRow(),
        product: { id: item.product_id, name: item.product_name, sku: item.sku, category: item.category, unit: item.unit },
        productSearch: item.product_name,
        requested_qty: item.requested_qty ?? null,
        available_stock: item.available_stock ?? null,
        qty: item.qty,
        branch_capital: item.branch_capital || 0,
        global_cost_price: item.branch_capital || 0,
        is_branch_specific_cost: false,
        transfer_capital: String(item.transfer_capital || item.branch_capital || ''),
        branch_retail: item.show_retail === false ? '' : String(item.branch_retail || ''),
        override: false, override_reason: '',
      }));
      setRows(editRows.length ? editRows : [newRow()]);
      setTab('new');
      toast.success(`Transfer pre-filled from ${transferData.po_number}! Review quantities and set pricing.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate transfer');
    }
    setGeneratingTransfer(null);
  };

  // ── Request Stock form helpers ────────────────────────────────────────────
  // Uses SmartProductSearch (same component as /sales-new Detailed Sales) for
  // each empty row: smart positioning, ↑/↓ keyboard nav, Enter-to-select,
  // scroll-into-center, fuzzy fallback, offline IndexedDB fallback.
  const newReqRow = () => ({ id: Date.now() + Math.random(), product: null, qty: '' });
  const updateReqRow = (id, updates) => setReqRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  const addReqRow = () => setReqRows(prev => [...prev, newReqRow()]);
  const removeReqRow = (id) => setReqRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);

  // Called when SmartProductSearch on an empty row picks a product.
  // Mirrors UnifiedSalesPage.handleProductSelect:
  //   1. Fill the row
  //   2. If it was the last row, append a new empty row (auto-grow)
  //   3. Focus the qty input on this row so user can immediately type quantity
  const selectReqProduct = (rowId, product) => {
    setReqRows(prev => {
      const idx = prev.findIndex(r => r.id === rowId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], product, qty: next[idx].qty || '1' };
      // Auto-add a fresh empty row when the LAST row gets filled
      if (idx === next.length - 1) next.push(newReqRow());
      return next;
    });
    // Focus this row's qty input after React commits the new state
    setTimeout(() => reqQtyRefs.current[rowId]?.focus?.(), 60);
  };

  const resetReqForm = () => {
    setReqTargetBranch('');
    setReqRows([newReqRow()]);
    setReqNotes('');
  };

  const handleSendRequest = async () => {
    if (!reqTargetBranch) { toast.error('Select a branch to request stock from'); return; }
    const validRows = reqRows.filter(r => r.product && parseFloat(r.qty) > 0);
    if (!validRows.length) { toast.error('Add at least one product with quantity'); return; }
    const targetBranch = branches.find(b => b.id === reqTargetBranch);
    setReqSaving(true);
    try {
      const items = validRows.map(r => ({
        product_id: r.product.id,
        product_name: r.product.name,
        sku: r.product.sku || '',
        unit: r.product.unit || '',
        quantity: parseFloat(r.qty),
        unit_price: 0,
        discount_type: 'none',
        discount_value: 0,
      }));
      await api.post('/purchase-orders', {
        vendor: `Branch Request → ${targetBranch?.name || reqTargetBranch}`,
        items,
        po_type: 'branch_request',
        branch_id: currentBranch?.id || user?.branch_id || '',
        supply_branch_id: reqTargetBranch,
        show_retail: reqShowRetail,
        purchase_date: localTodayStr(),
        notes: reqNotes,
      });
      toast.success(`Stock request sent to ${targetBranch?.name}! They will be notified.`);
      resetReqForm();
      setTab('history');
      // BT Batch 2 P3: the legacy Requests tab is retired — one front door.
      navigate('/stock-requests');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    }
    setReqSaving(false);
  };

  // Load outgoing requests (requests THIS branch sent to other branches)
  const loadOutgoingRequests = useCallback(async () => {
    const branchId = selectedBranchId || currentBranch?.id;
    if (!branchId) return;
    setOutgoingLoading(true);
    try {
      const res = await api.get('/purchase-orders', { params: { po_type: 'branch_request', branch_id: branchId, limit: 100 } });
      setOutgoingRequests(res.data.purchase_orders || []);
    } catch { setOutgoingRequests([]); }
    setOutgoingLoading(false);
  }, [selectedBranchId, currentBranch]);

  useEffect(() => {
    if (tab === 'history' && historyTab === 'requests' && requestsView === 'outgoing') loadOutgoingRequests();
  }, [tab, historyTab, requestsView, loadOutgoingRequests]);

  // ── Row helpers ─────────────────────────────────────────────────────────────
  const updateRow = (id, updates) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));

  const handleRowSearchKeyDown = (e, row) => {
    const matches = row.productMatches || [];
    if (!matches.length) {
      if (e.key === 'Escape') updateRow(row.id, { productSearch: '', productMatches: [], activeSearchIndex: -1 });
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      updateRow(row.id, { activeSearchIndex: Math.min((row.activeSearchIndex ?? -1) + 1, matches.length - 1) });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      updateRow(row.id, { activeSearchIndex: Math.max((row.activeSearchIndex ?? 0) - 1, 0) });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Audit BT-FLOW-2: first Enter used to be dead (nothing preselected).
      // Sales parity: no arrow-key selection yet -> take the TOP match.
      const idx = row.activeSearchIndex ?? -1;
      const pick = (idx >= 0 && matches[idx]) ? matches[idx] : matches[0];
      if (pick) selectProduct(row.id, pick);
    } else if (e.key === 'Escape') {
      updateRow(row.id, { productMatches: [], activeSearchIndex: -1 });
    }
  };

  function applyMarkupToRow(row, markups) {
    if (!row.product) return row;
    const cat = row.product.category || 'General';
    const rule = markups.find(m => m.category === cat);
    if (!rule) return row;
    const bc = parseFloat(row.branch_capital) || 0;
    const type = rule.type;
    const val = parseFloat(rule.value) || 0;
    const tc = type === 'percent' ? Math.round(bc * (1 + val / 100) * 100) / 100
                                   : Math.round((bc + val) * 100) / 100;
    return { ...row, transfer_capital: String(tc) };
  }

  // ── Suggested transfer lines (P7, hydration #7) ──
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  useEffect(() => {
    let stale = false;
    setSuggestions([]);
    if (!fromBranchId || !toBranchId || fromBranchId === toBranchId) return;
    setSuggestionsLoading(true);
    api.get('/branch-transfers/suggestions', {
      params: { from_branch_id: fromBranchId, to_branch_id: toBranchId },
    }).then(res => {
      if (!stale) setSuggestions(res.data?.suggestions || []);
    }).catch(() => { /* advisory only */ })
      .finally(() => { if (!stale) setSuggestionsLoading(false); });
    return () => { stale = true; };
  }, [fromBranchId, toBranchId]);

  const addSuggestion = async (s) => {
    try {
      const res = await api.get('/branch-transfers/product-lookup', {
        params: { q: s.sku || s.product_name, from_branch_id: fromBranchId, to_branch_id: toBranchId },
      });
      const match = (res.data || []).find(m => m.id === s.product_id) || (res.data || [])[0];
      if (!match) { toast.error('Product not found in lookup'); return; }
      let targetId = null;
      setRows(prev => {
        if (prev.some(r => r.product && r.product.id === match.id)) return prev;
        const next = [...prev];
        let idx = next.findIndex(r => !r.product);
        if (idx === -1) { next.push(newRow()); idx = next.length - 1; }
        targetId = next[idx].id;
        return next;
      });
      setTimeout(() => { if (targetId) selectProduct(targetId, match); }, 30);
    } catch {
      toast.error('Could not load the product');
    }
  };

  const offlineToastShown = useRef(false);
  const searchProduct = (rowId, q) => {
    updateRow(rowId, { productSearch: q, product: null });
    if (searchTimers.current[rowId]) clearTimeout(searchTimers.current[rowId]);
    if (!q || q.length < 1) { updateRow(rowId, { productMatches: [] }); return; }
    searchTimers.current[rowId] = setTimeout(async () => {
      try {
        const params = { q, from_branch_id: fromBranchId, to_branch_id: toBranchId };
        const res = await api.get('/branch-transfers/product-lookup', { params });
        updateRow(rowId, { productMatches: res.data || [] });
      } catch {
        // P7 (hydration #6): flaky internet must not freeze the encoder —
        // fall back to the offline product cache (name/sku/barcode contains).
        // Cached rows lack the transfer pricing payload, so capitals start
        // blank and the operator fills them in.
        try {
          const cached = await getOfflineProducts();
          const ql = q.toLowerCase();
          const matches = (cached || [])
            .filter(pr => pr.active !== false && !pr.is_repack)
            .filter(pr => (pr.name || '').toLowerCase().includes(ql)
              || (pr.sku || '').toLowerCase().includes(ql)
              || (pr.barcode || '').startsWith(q))
            .slice(0, 10)
            .map(pr => ({
              id: pr.id, name: pr.name, sku: pr.sku || '', category: pr.category || '',
              barcode: pr.barcode || '',
              branch_capital: parseFloat(pr.cost_price) || 0,
              last_branch_retail: (pr.prices || {}).retail ?? null,
              repacks: [], _offline: true,
            }));
          updateRow(rowId, { productMatches: matches });
          if (matches.length && !offlineToastShown.current) {
            offlineToastShown.current = true;
            toast.info('Offline — showing cached products. Check the pricing before sending.');
          }
        } catch { /* no cache either */ }
      }
    }, 200);
  };

  const selectProduct = (rowId, p) => {
    const rowUpdate = {
      product: p, productSearch: p.name, productMatches: [], activeSearchIndex: -1,
      branch_capital: p.branch_capital,
      global_cost_price: p.global_cost_price ?? p.branch_capital,
      is_branch_specific_cost: p.is_branch_specific_cost ?? false,
      last_purchase_ref: p.last_purchase_ref,
      moving_average_ref: p.moving_average_ref,
      last_branch_retail: p.last_branch_retail,
      branch_retail: p.last_branch_retail != null ? String(p.last_branch_retail) : '',
      // Target-branch snapshot (helps the sender decide pricing)
      target_branch_capital: p.target_branch_capital ?? 0,
      target_branch_moving_average: p.target_branch_moving_average ?? 0,
      target_branch_retail: p.target_branch_retail ?? 0,
      target_branch_stock: p.target_branch_stock ?? 0,
      // Repack children with their capital + current dest price
      repacks: (p.repacks || []).map(rp => ({
        ...rp,
        new_retail_price: '', // blank = no change
      })),
    };
    // Apply markup to get transfer capital
    const cat = p.category || 'General';
    const rule = categoryMarkups.find(m => m.category === cat);
    if (rule) {
      const bc = p.branch_capital;
      const val = parseFloat(rule.value) || 0;
      const tc = rule.type === 'percent'
        ? Math.round(bc * (1 + val / 100) * 100) / 100
        : Math.round((bc + val) * 100) / 100;
      rowUpdate.transfer_capital = String(tc);
    } else {
      rowUpdate.transfer_capital = String(p.branch_capital);
    }
    updateRow(rowId, rowUpdate);
    // Sales-parity flow (audit BT-FLOW-1/BT-FLOW-3, same pattern as the
    // Request-Stock grid's selectReqProduct): auto-grow when the LAST row
    // fills, then hand focus straight to this row's Qty input.
    setRows(rs => (rs[rs.length - 1]?.id === rowId ? [...rs, newRow()] : rs));
    setTimeout(() => qtyRefs.current[rowId]?.focus?.(), 60);
  };

  // ── Category markup panel ───────────────────────────────────────────────────
  const setMarkup = (category, field, value) => {
    setCategoryMarkups(prev => {
      const existing = prev.find(m => m.category === category);
      if (existing) {
        return prev.map(m => m.category === category ? { ...m, [field]: value } : m);
      }
      return [...prev, { category, type: 'fixed', value: 0, [field]: value }];
    });
  };
  const getMarkup = (category) => categoryMarkups.find(m => m.category === category) || { type: 'fixed', value: '' };

  const saveTemplate = async () => {
    if (!toBranchId) return;
    try {
      await api.put(`/branch-transfers/markup-template/${toBranchId}`, {
        min_margin: parseFloat(minMargin) || 0,
        category_markups: categoryMarkups.filter(m => m.value !== '' && parseFloat(m.value) > 0),
      });
      toast.success('Markup template saved');
    } catch { toast.error('Failed to save template'); }
  };

  // ── Park / Resume — Draft transfer staging ────────────────────────────────
  const loadParkedTransfers = useCallback(async () => {
    if (!fromBranchId) { setParkedTransfers([]); return; }
    try {
      const res = await api.get('/parked-branch-transfers', { params: { from_branch_id: fromBranchId } });
      setParkedTransfers(res.data?.parks || []);
    } catch { /* silent */ }
  }, [fromBranchId]);

  // Load park count whenever from-branch changes
  useEffect(() => { loadParkedTransfers(); }, [loadParkedTransfers]);

  const handlePark = async () => {
    if (!fromBranchId) { toast.error('Source branch required'); return; }
    const populated = rows.filter(r => r.product);
    if (!populated.length) { toast.error('Nothing to park — add at least one product'); return; }

    setParkingNow(true);
    try {
      const payload = {
        id: activeParkId || undefined,
        from_branch_id: fromBranchId,
        to_branch_id: toBranchId || '',
        label: parkLabel.trim() || (toBranchId ? `To ${branches.find(b => b.id === toBranchId)?.name || 'branch'}` : 'Draft'),
        rows,
        min_margin: parseFloat(minMargin) || 0,
        category_markups: categoryMarkups,
        item_count: populated.length,
      };
      await api.post('/parked-branch-transfers', payload);
      toast.success(activeParkId ? 'Park updated — pick it up later' : 'Transfer parked — pick it up later');
      setParkDialogOpen(false);
      setParkLabel('');
      setActiveParkId(null);
      // Reset to a fresh draft
      setRows([newRow()]);
      setToBranchId('');
      await loadParkedTransfers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to park transfer');
    }
    setParkingNow(false);
  };

  const handleResume = async (parkId) => {
    try {
      const res = await api.post(`/parked-branch-transfers/${parkId}/consume`);
      const doc = res.data;
      // Skip the source-branch reset effect when we set toBranchId so rows aren't wiped
      skipResetRef.current = true;
      setFromBranchId(doc.from_branch_id || fromBranchId);
      setToBranchId(doc.to_branch_id || '');
      setRows((doc.rows && doc.rows.length) ? doc.rows : [newRow()]);
      setMinMargin(doc.min_margin ?? 20);
      setCategoryMarkups(doc.category_markups || []);
      setActiveParkId(parkId); // remember so re-park overwrites instead of creating new
      setResumeDialogOpen(false);
      toast.success('Resumed — picked up where you left off');
      await loadParkedTransfers();
      // Allow effects again on next user-driven branch change
      setTimeout(() => { skipResetRef.current = false; }, 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resume');
      await loadParkedTransfers();
    }
  };

  const handleDiscardPark = async (parkId) => {
    if (!window.confirm('Discard this parked transfer? This cannot be undone.')) return;
    try {
      await api.delete(`/parked-branch-transfers/${parkId}`);
      toast.success('Discarded');
      await loadParkedTransfers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to discard');
    }
  };



  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (opts = {}) => {
    // opts may be a SyntheticEvent if called directly from <Button onClick>; defensive coercion.
    const requiresApproval = (opts && typeof opts === 'object' && opts.requiresApproval === true);

    if (!toBranchId) { toast.error('Select a destination branch'); return; }
    if (!rows.length || rows.every(r => !r.product)) { toast.error('Add at least one product'); return; }

    const validRows = rows.filter(r => r.product);
    const blockers = validRows.filter(r => {
      const v = validateRow(r, minMargin);
      // When submitting for approval, retail==0 is allowed (admin will set it)
      if (requiresApproval && v.reason === 'retail') return false;
      return !v.ok && !r.override;
    });
    if (blockers.length > 0) {
      toast.error(`${blockers.length} row(s) have validation errors. Fix or override them.`);
      return;
    }

    setSaving(true);
    try {
      // Build repack_price_updates from rows that have repack price entries
      const repack_price_updates = [];
      validRows.forEach(r => {
        (r.repacks || []).forEach(rp => {
          if (rp.new_retail_price && parseFloat(rp.new_retail_price) > 0) {
            repack_price_updates.push({
              repack_id: rp.id,
              repack_name: rp.name,
              new_retail_price: parseFloat(rp.new_retail_price),
              units_per_parent: rp.units_per_parent,
              capital_per_repack: rp.capital_per_repack,
              current_dest_retail: rp.current_dest_retail,
              parent_product_id: r.product.id,
              parent_product_name: r.product.name,
            });
          }
        });
      });

      await api.post('/branch-transfers', {
        from_branch_id: fromBranchId,
        to_branch_id: toBranchId,
        min_margin: parseFloat(minMargin) || 0,
        requires_approval: requiresApproval,
        category_markups: categoryMarkups.filter(m => parseFloat(m.value) > 0),
        repack_price_updates,
        request_po_id: requestContext?.po_id || '',
        request_po_number: requestContext?.po_number || '',
        items: validRows.map(r => ({
          product_id: r.product.id,
          product_name: r.product.name,
          sku: r.product.sku,
          category: r.product.category,
          unit: r.product.unit,
          qty: parseFloat(r.qty) || 0,
          requested_qty: r.requested_qty || null,
          branch_capital: r.branch_capital,
          transfer_capital: parseFloat(r.transfer_capital) || 0,
          branch_retail: parseFloat(r.branch_retail) || 0,
          last_purchase_ref: r.last_purchase_ref,
          moving_average_ref: r.moving_average_ref,
          override: r.override,
          override_reason: r.override_reason,
        })),
      });
      toast.success(requiresApproval ? 'Submitted for admin approval — admin notified by SMS' : 'Branch transfer order created!');
      setRows([newRow()]);
      setRequestContext(null);
      setTab('history');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create transfer'); }
    setSaving(false);
  };

  // ── Edit existing draft order ──────────────────────────────────────────────
  // Rows rebuild from the stored lines, then hydrate from /line-prefill so the
  // Edit door prices exactly like product search (owner regression 2026-09-04:
  // a triage-born draft opened with ₱0 capital / transfer capital / retail
  // because Edit trusted the stored line). Rule per line:
  //   * reference columns (capital, MA/LP, last retail, target snapshot) ALWAYS refresh
  //   * a transfer capital / retail a PERSON saved is kept
  //   * a zero or machine-prefilled one is re-suggested: sending-branch capital
  //     + category markup → transfer capital; last transfer retail INTO the
  //     receiving branch → retail
  // MA / LP are reference chips only — they never drive a price.
  const editHydrationRef = useRef(0);
  const loadOrderIntoEdit = async (order) => {
    skipResetRef.current = true;
    setFromBranchId(order.from_branch_id || '');
    setToBranchId(order.to_branch_id || '');
    setMinMargin(order.min_margin ?? 20);
    setCategoryMarkups(order.category_markups || []);
    setEditingOrderId(order.id);
    // Reconstruct rows from stored items
    const editRows = (order.items || []).map(item => ({
      ...newRow(),
      product: { id: item.product_id, name: item.product_name, sku: item.sku, category: item.category, unit: item.unit },
      productSearch: item.product_name,
      qty: item.qty,
      branch_capital: item.branch_capital || 0,
      global_cost_price: item.branch_capital || 0,
      is_branch_specific_cost: false,
      transfer_capital: String(item.transfer_capital || ''),
      branch_retail: String(item.branch_retail || ''),
      override: item.override || false,
      override_reason: item.override_reason || '',
    }));
    setRows(editRows.length ? editRows : [newRow()]);
    setTab('new');

    const token = ++editHydrationRef.current;
    const productIds = (order.items || []).map(i => i.product_id).filter(Boolean);
    if (!productIds.length || !order.from_branch_id || !order.to_branch_id) return;
    try {
      const hasMarkups = (order.category_markups || []).length > 0;
      const [prefillRes, templateRes] = await Promise.all([
        api.post('/branch-transfers/line-prefill', {
          from_branch_id: order.from_branch_id, to_branch_id: order.to_branch_id, product_ids: productIds,
        }),
        // A draft with no markups of its own uses the destination's saved template (new-transfer parity)
        hasMarkups ? Promise.resolve(null)
                   : api.get(`/branch-transfers/markup-template/${order.to_branch_id}`).catch(() => null),
      ]);
      if (token !== editHydrationRef.current) return; // the user opened something else meanwhile
      const markups = hasMarkups ? (order.category_markups || []) : (templateRes?.data?.category_markups || []);
      const byId = {};
      (prefillRes.data?.items || []).forEach(p => { byId[p.id] = p; });
      const storedById = {};
      (order.items || []).forEach(i => { storedById[i.product_id] = i; });
      let refreshed = 0;
      const hydrated = editRows.map(row => {
        const p = row.product && byId[row.product.id];
        if (!p) return row;
        const stored = storedById[p.id] || {};
        const machine = stored.price_source === 'prefill';
        const storedTc = parseFloat(stored.transfer_capital) || 0;
        const storedBr = parseFloat(stored.branch_retail) || 0;
        const next = {
          ...row,
          product: { ...row.product, sku: row.product.sku || p.sku, category: row.product.category || p.category, unit: row.product.unit || p.unit },
          branch_capital: p.branch_capital,
          global_cost_price: p.global_cost_price ?? p.branch_capital,
          is_branch_specific_cost: p.is_branch_specific_cost ?? false,
          last_purchase_ref: p.last_purchase_ref,
          moving_average_ref: p.moving_average_ref,
          last_branch_retail: p.last_branch_retail,
          target_branch_capital: p.target_branch_capital ?? 0,
          target_branch_moving_average: p.target_branch_moving_average ?? 0,
          target_branch_retail: p.target_branch_retail ?? 0,
          target_branch_stock: p.target_branch_stock ?? 0,
        };
        if (storedTc > 0 && !machine) {
          next.transfer_capital = String(storedTc);
        } else {
          next.transfer_capital = applyMarkupToRow({ ...next, transfer_capital: String(p.branch_capital) }, markups).transfer_capital;
          refreshed += 1;
        }
        next.branch_retail = (storedBr > 0 && !machine)
          ? String(storedBr)
          : (p.last_branch_retail != null ? String(p.last_branch_retail) : '');
        return next;
      });
      if (!hasMarkups) {
        skipMarkupReapplyRef.current = true; // rows below already carry these markups
        setCategoryMarkups(markups);
      }
      setRows(hydrated);
      toast.success(refreshed
        ? `Prices loaded from the sending branch for ${refreshed} line${refreshed === 1 ? '' : 's'} — review before sending`
        : 'Reference prices refreshed — your saved prices were kept');
    } catch (e) {
      if (token !== editHydrationRef.current) return;
      toast.error('Could not load prices. Check capital and retail before sending.');
    }
  };

  const handleUpdateDraft = async () => {
    if (!editingOrderId) { handleSubmit(); return; }
    if (!rows.length || rows.every(r => !r.product)) { toast.error('Add at least one product'); return; }
    const validRows = rows.filter(r => r.product);
    const blockers = validRows.filter(r => { const v = validateRow(r, minMargin); return !v.ok && !r.override; });
    if (blockers.length > 0) { toast.error(`${blockers.length} row(s) have validation errors.`); return; }

    setSaving(true);
    try {
      await api.put(`/branch-transfers/${editingOrderId}`, {
        min_margin: parseFloat(minMargin) || 0,
        category_markups: categoryMarkups.filter(m => parseFloat(m.value) > 0),
        items: validRows.map(r => ({
          product_id: r.product.id,
          product_name: r.product.name,
          sku: r.product.sku,
          category: r.product.category,
          unit: r.product.unit,
          qty: parseFloat(r.qty) || 0,
          branch_capital: r.branch_capital,
          transfer_capital: parseFloat(r.transfer_capital) || 0,
          branch_retail: parseFloat(r.branch_retail) || 0,
          override: r.override,
          override_reason: r.override_reason,
        })),
      });
      toast.success('Draft transfer updated!');
      setEditingOrderId(null);
      setRows([newRow()]);
      setTab('history');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update transfer'); }
    setSaving(false);
  };

  const cancelEdit = () => {
    setEditingOrderId(null);
    setRequestContext(null);
    setRows([newRow()]);
    setFromBranchId(currentBranch?.id || '');
    setToBranchId('');
  };
  const [receiveQtys, setReceiveQtys] = useState({});
  const [receiveNotes, setReceiveNotes] = useState('');
  const [receiveConfirmStep, setReceiveConfirmStep] = useState(false); // double-check step (variance)
  const [receiveStep, setReceiveStep] = useState('qty'); // 'qty' | 'upload' | 'summary'
  const [acceptDialog, setAcceptDialog] = useState(null); // order to accept
  const [disputeDialog, setDisputeDialog] = useState(null); // order to dispute
  const [disputeNote, setDisputeNote] = useState('');
  const [actionSaving, setActionSaving] = useState(false);
  // Variance PIN gate dialog — opens when capital loss > threshold
  const [variancePinDialog, setVariancePinDialog] = useState(null); // { orderId, note, action, totals: varianceTotals(order) }
  const [variancePin, setVariancePin] = useState('');
  const [variancePinError, setVariancePinError] = useState('');
  const [variancePinSaving, setVariancePinSaving] = useState(false);
  const [btUploadQROpen, setBtUploadQROpen] = useState(false);
  const [btUploadOrderId, setBtUploadOrderId] = useState(null);
  const [btVerifyOpen, setBtVerifyOpen] = useState(false);
  const [btVerifyId, setBtVerifyId] = useState(null);
  const [btViewQROpen, setBtViewQROpen] = useState(false);
  const [receiveReceiptData, setReceiveReceiptData] = useState(null);

  const openReceive = (order) => {
    setViewOrder(order);
    const qtys = {};
    order.items.forEach(item => { qtys[item.product_id] = item.qty; });
    setReceiveQtys(qtys);
    setReceiveNotes('');
    setReceiveConfirmStep(false);
    setReceiveReceiptData(null);
    setReceiveStep('qty'); // reset wizard to first step
    setReceiveDialog(true);
  };

  // Compute variance for current receiveQtys
  const getVariances = (items) => {
    if (!items) return { hasVariance: false, shortages: [], excesses: [] };
    const shortages = [], excesses = [];
    items.forEach(item => {
      const ordered = parseFloat(item.qty) || 0;
      const received = parseFloat(receiveQtys[item.product_id] ?? ordered) || 0;
      const diff = received - ordered;
      if (diff < 0) shortages.push({ ...item, qty_received: received, diff });
      else if (diff > 0) excesses.push({ ...item, qty_received: received, diff });
    });
    return { hasVariance: shortages.length > 0 || excesses.length > 0, shortages, excesses };
  };

  const handleReceive = async () => {
    if (!viewOrder) return;

    const { hasVariance } = getVariances(viewOrder.items);

    // Soft warning if no receipt — but allow proceeding (one-time confirm).
    // Receipt is recommended for audit trail but not blocking.
    if (!receiveReceiptData?.fileCount && !receiveConfirmStep) {
      const ok = window.confirm(
        'No receipt / DR photo uploaded.\n\n' +
        'A receipt photo is recommended for audit trail. You can scan the QR code with your phone or upload from this computer using the Upload Receipt section above.\n\n' +
        'Proceed without a receipt?'
      );
      if (!ok) return;
    }

    // First click with variance: show double-check step
    if (hasVariance && !receiveConfirmStep) {
      setReceiveConfirmStep(true);
      return;
    }

    // Build items payload. Blank qty fields are REJECTED, not silently
    // defaulted — `parseFloat('') ?? x` never falls back (NaN passes ??), and
    // the backend would treat the null as "received in full" and move stock.
    const blankLines = viewOrder.items.filter(
      item => !Number.isFinite(parseFloat(receiveQtys[item.product_id]))
    );
    if (blankLines.length > 0) {
      toast.error(`Enter the received quantity for: ${blankLines.map(i => i.product_name).slice(0, 3).join(', ')}${blankLines.length > 3 ? '…' : ''}`);
      return;
    }
    const items = viewOrder.items.map(item => ({
      product_id: item.product_id,
      qty: item.qty,
      qty_received: parseFloat(receiveQtys[item.product_id]),
      transfer_capital: item.transfer_capital,
      branch_retail: item.branch_retail,
    }));

    // WS2 — the Price Math step is ALWAYS shown: the backend previews the
    // capital its pricing law will write (higher→replace, lower→MA cushion)
    // and asks the receiver to confirm the retail. What's confirmed here is
    // exactly what _apply_receipt writes.
    try {
      const preview = await api.get(`/branch-transfers/${viewOrder.id}/capital-preview`);
      const retails = {};
      (preview.data.items || []).forEach(i => {
        retails[i.product_id] = i.suggested_retail > 0 ? String(i.suggested_retail) : '';
      });
      setTransferCapitalChoices({});   // org pricing law by default; radios override per line
      setTransferRetailChoices(retails);
      setTransferCapitalPreview(preview.data);
      setTransferCapitalPendingItems(items);
      setTransferCapitalDialog(true);
      return;
    } catch { /* preview unavailable — receive directly, backend law still applies */ }

    await _doReceive(items, {}, {});
  };

  const _doReceive = async (items, capitalChoices, retailChoices = {}) => {
    setReceiveSaving(true);
    try {
      const retail_choices = {};
      Object.entries(retailChoices || {}).forEach(([pid, v]) => {
        const n = parseFloat(v);
        if (Number.isFinite(n) && n > 0) retail_choices[pid] = n;
      });
      const payload = {
        items,
        notes: receiveNotes,
        capital_choices: capitalChoices,
        retail_choices,
      };
      if (receiveReceiptData?.sessionId) {
        payload.upload_session_ids = [receiveReceiptData.sessionId];
      }
      const res = await api.post(`/branch-transfers/${viewOrder.id}/receive`, payload);
      if (res.data.status === 'received_pending') {
        toast.warning('Quantities have variance — submitted for source branch confirmation.');
      } else {
        toast.success(res.data.message || 'Transfer received!');
        // Notify PriceScanManager to re-fetch capital change alerts immediately
        window.dispatchEvent(new CustomEvent('agribooks:capital_changed'));
      }
      setReceiveDialog(false);
      setReceiveConfirmStep(false);
      setTransferCapitalDialog(false);
      setTransferCapitalPreview(null);
      setViewOrder(null);
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Receive failed'); }
    setReceiveSaving(false);
  };

  const confirmTransferReceive = async () => {
    if (!transferCapitalPendingItems) return;
    await _doReceive(transferCapitalPendingItems, transferCapitalChoices, transferRetailChoices);
  };

  const handleAcceptReceipt = async (orderId, note = '', action = 'accept', pin = '') => {
    setActionSaving(true);
    try {
      const payload = { note, action };
      if (pin) payload.pin = pin;
      const res = await api.post(`/branch-transfers/${orderId}/accept-receipt`, payload);
      if (action === 'accept_with_incident') {
        toast.success(`Receipt accepted. Incident ticket ${res.data.incident_ticket_number} created for investigation.`);
      } else {
        toast.success('Receipt accepted — inventory updated.');
        window.dispatchEvent(new CustomEvent('agribooks:capital_changed'));
      }
      setAcceptDialog(null);
      setVariancePinDialog(null);
      setVariancePin('');
      setVariancePinError('');
      loadOrders();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && detail.includes('PIN/TOTP is required')) {
        // Amounts come from the order on screen, not from parsing the error
        // text — that only ever carried the LOSS half, so an excess-only
        // variance read "Capital loss: ₱0.00" on a ₱45,480 PIN prompt.
        setVariancePinDialog({ orderId, note, action, totals: varianceTotals(acceptDialog) });
        setVariancePin('');
        setVariancePinError('');
        setAcceptDialog(null);
      } else if (e.response?.status === 403 && detail.includes('Invalid PIN')) {
        setVariancePinError('Invalid PIN — use source-branch manager PIN, admin PIN, or TOTP.');
      } else {
        toast.error(detail || 'Failed to accept');
      }
    }
    setActionSaving(false);
  };

  const handleAcceptWithPin = async () => {
    if (!variancePinDialog || !variancePin.trim()) {
      setVariancePinError('PIN is required.');
      return;
    }
    setVariancePinSaving(true);
    setVariancePinError('');
    try {
      const { orderId, note, action } = variancePinDialog;
      const res = await api.post(`/branch-transfers/${orderId}/accept-receipt`, { note, action, pin: variancePin.trim() });
      if (action === 'accept_with_incident') {
        toast.success(`Receipt accepted. Incident ticket ${res.data.incident_ticket_number} created for investigation.`);
      } else {
        toast.success('Receipt accepted — inventory updated.');
      }
      setVariancePinDialog(null);
      setVariancePin('');
      setVariancePinError('');
      loadOrders();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 403 || detail.includes('Invalid PIN')) {
        setVariancePinError('Invalid PIN — use source-branch manager PIN, admin PIN, or TOTP.');
      } else {
        toast.error(detail || 'Failed to accept');
      }
    }
    setVariancePinSaving(false);
  };

  const handleDisputeReceipt = async () => {
    if (!disputeDialog || !disputeNote.trim()) { toast.error('Dispute reason is required'); return; }
    setActionSaving(true);
    try {
      await api.post(`/branch-transfers/${disputeDialog.id}/dispute-receipt`, { note: disputeNote });
      toast.success('Receipt disputed — destination has been notified to re-count.');
      setDisputeDialog(null);
      setDisputeNote('');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to dispute'); }
    setActionSaving(false);
  };

  const handleSend = async (orderId) => {
    try {
      await api.post(`/branch-transfers/${orderId}/send`);
      toast.success('Transfer marked as sent');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleResubmit = async (orderId) => {
    if (!window.confirm('Resubmit this transfer for admin approval? Admins will be notified by SMS.')) return;
    try {
      await api.post(`/branch-transfers/${orderId}/resubmit`);
      toast.success('Resubmitted — admins notified');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resubmit'); }
  };

  const sendTransferToTerminal = async (orderId) => {
    if (!window.confirm('Send this transfer to the terminal for receiving? It will be locked here until the terminal processes it.')) return;
    try {
      await api.post(`/branch-transfers/${orderId}/send-to-terminal`);
      toast.success('Transfer sent to terminal for checking');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send to terminal'); }
  };

  // ── Print via centralized PrintEngine ──────────────────────────────────────
  const [bizInfo, setBizInfo] = useState({});
  useEffect(() => {
    api.get('/settings/business-info').then(r => setBizInfo(r.data)).catch(() => {});
  }, []);

  const printTransferOrder = async (order, format = 'dot_matrix') => {
    const toBranch = branches.find(b => b.id === order.to_branch_id)?.name || '';
    const fromBranch = branches.find(b => b.id === order.from_branch_id)?.name || '';

    // Generate or fetch document code for QR
    let docCode = order.doc_code || '';
    if (!docCode) {
      try {
        const res = await api.post('/doc/generate-code', { doc_type: 'branch_transfer', doc_id: order.id });
        docCode = res.data.code || '';
      } catch { /* print without QR if code generation fails */ }
    }

    PrintEngine.print({
      type: 'branch_transfer',
      data: { ...order, from_branch_name: fromBranch, to_branch_name: toBranch },
      format,
      businessInfo: bizInfo,
      docCode,
    });
  };

  // ── Send to Print (receiver stations + terminals) ──────────────────────────
  // One shared modal for both transfer orders and stock requests.
  // target = { kind: 'transfer' | 'request', doc }
  const [sendToPrintOpen, setSendToPrintOpen] = useState(false);
  const [sendToPrintTarget, setSendToPrintTarget] = useState(null);
  const openSendToPrint = (kind, doc) => { setSendToPrintTarget({ kind, doc }); setSendToPrintOpen(true); };

  // Generate HTML for Remote Print — mirrors printTransferOrder's data prep.
  const getTransferRemoteHtml = (order, format) => {
    const toBranch = branches.find(b => b.id === order.to_branch_id)?.name || '';
    const fromBranch = branches.find(b => b.id === order.from_branch_id)?.name || '';
    return PrintEngine.generateHtml({
      type: 'branch_transfer',
      data: { ...order, from_branch_name: fromBranch, to_branch_name: toBranch },
      format,
      businessInfo: bizInfo,
      docCode: order.doc_code || '',
    });
  };

  // Generate HTML for Remote Print — mirrors handlePrintRequest's data prep.
  const getRequestRemoteHtml = (req, format) => {
    const requesting = branches.find(b => b.id === req.branch_id)?.name || 'Requesting Branch';
    const supplying  = branches.find(b => b.id === req.supply_branch_id)?.name || 'Supplying Branch';
    return PrintEngine.generateHtml({
      type: 'purchase_order',
      data: {
        ...req,
        vendor: `Stock Request from ${requesting}`,
        supply_branch_name: supplying,
        order_date: req.purchase_date || req.created_at,
      },
      format,
      businessInfo: bizInfo,
      docCode: req.doc_code || '',
    });
  };

  const handleCancel = async (orderId) => {    if (!window.confirm('Cancel this transfer order?')) return;
    try {
      await api.delete(`/branch-transfers/${orderId}`);
      toast.success('Transfer cancelled');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ── Stock-request actions (Incoming / Outgoing tabs under Requests) ─────────
  // Print the stock request through PrintEngine using the same 3 formats as
  // other documents (full / Thermal / dot-matrix). Stock requests are purchase
  // orders with po_type="branch_request", so we re-use the purchase_order
  // print pathway.
  const handlePrintRequest = async (req, format) => {
    const requesting = branches.find(b => b.id === req.branch_id)?.name || 'Requesting Branch';
    const supplying  = branches.find(b => b.id === req.supply_branch_id)?.name || 'Supplying Branch';
    let docCode = req.doc_code || '';
    if (!docCode) {
      try {
        const res = await api.post('/doc/generate-code', { doc_type: 'purchase_order', doc_id: req.id });
        docCode = res.data.code || '';
      } catch { /* print without QR if generation fails */ }
    }
    PrintEngine.print({
      type: 'purchase_order',
      data: {
        ...req,
        vendor: `Stock Request from ${requesting}`,
        supply_branch_name: supplying,
        order_date: req.purchase_date || req.created_at,
      },
      format,
      businessInfo: bizInfo,
      docCode,
    });
  };

  // Cancel a stock request. Backend requires manager PIN (same as PO cancel).
  // Phase 0.5: Removed the hard-coded `in_progress` guard — the backend
  // now decides based on whether a linked BTO exists. If a linked BTO
  // blocks cancel, the backend returns 400 with an actionable detail
  // pointing at the BTO number.
  const handleCancelRequest = async (req) => {
    // Status guard for genuinely terminal states; in_progress is now allowed.
    if (req.status === 'fulfilled' || req.status === 'partially_fulfilled' || req.status === 'cancelled') {
      toast.error(`Cannot cancel — status is "${req.status}"`);
      return;
    }
    if (!window.confirm(`Cancel stock request ${req.po_number}?`)) return;
    const pin = window.prompt('Enter manager PIN to cancel:');
    if (!pin) return;
    try {
      await api.delete(`/purchase-orders/${req.id}`, { data: { pin } });
      toast.success('Stock request cancelled');
      loadOrders();
    } catch (e) {
      // If backend points at a linked BTO, surface that to the operator.
      const detail = e.response?.data?.detail || 'Cancel failed';
      toast.error(detail);
    }
  };

  // Mark a partially-fulfilled stock request as complete with a reason
  const handleClosePartial = async () => {
    if (!closePartialTarget || !closePartialReason.trim()) return;
    setClosePartialSaving(true);
    try {
      await api.post(`/purchase-orders/${closePartialTarget.id}/close-partial`, {
        reason: closePartialReason.trim(),
      });
      toast.success(`Request ${closePartialTarget.po_number} marked as complete`);
      setClosePartialDialog(false);
      setClosePartialTarget(null);
      setClosePartialReason('');
      loadRequests();
      loadOutgoingRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close request');
    } finally {
      setClosePartialSaving(false);
    }
  };

  // ── Totals ─────────────────────────────────────────────────────────────────
  const validRows = rows.filter(r => r.product);
  const totalBranchCapital = validRows.reduce((s, r) => s + (r.branch_capital * (parseFloat(r.qty)||0)), 0);
  const totalTransfer = validRows.reduce((s, r) => s + ((parseFloat(r.transfer_capital)||0) * (parseFloat(r.qty)||0)), 0);
  const totalRetail = validRows.reduce((s, r) => s + ((parseFloat(r.branch_retail)||0) * (parseFloat(r.qty)||0)), 0);

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="branch-transfer-page">
      <StockMovementSwitcher current="transfers" />
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Building2 size={22} className="text-[#1A4D2E]" /> Branch Transfers
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Transfer stock between any branches with auto-computed pricing
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="new"><Plus size={14} className="mr-1" /> New Transfer</TabsTrigger>
          {/* Requesting stock lives on the Stock Requests page — reached from
              the StockMovementSwitcher above, never from a pseudo-tab here. */}
          <TabsTrigger value="history" data-testid="transfer-history-tab"><Clock size={14} className="mr-1" /> Transfers</TabsTrigger>
        </TabsList>

        {/* ── NEW TRANSFER TAB ── */}
        <TabsContent value="new" className="space-y-4 mt-4">
          {/* Editing banner */}
          {editingOrderId && (
            <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <AlertTriangle size={15} className="shrink-0" />
                <span>Editing draft order — changes won't affect inventory until it's sent and received.</span>
              </div>
              <Button variant="ghost" size="sm" onClick={cancelEdit} className="text-amber-700 h-7">
                Discard Changes
              </Button>
            </div>
          )}

          {/* Header row */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[160px]">
              <Label className="text-xs">From Branch <span className="text-slate-400">(source)</span></Label>
              <Select value={fromBranchId} onValueChange={val => { setFromBranchId(val); setToBranchId(''); }}
                data-testid="from-branch-select">
                <SelectTrigger className="mt-1 h-9" data-testid="from-branch-trigger">
                  <SelectValue placeholder="Select source branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.filter(b => b.id !== toBranchId).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center pb-1">
              <ArrowRight size={18} className="text-slate-400" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <Label className="text-xs">To Branch <span className="text-slate-400">(destination)</span></Label>
              <Select value={toBranchId} onValueChange={setToBranchId} disabled={!fromBranchId}>
                <SelectTrigger className="mt-1 h-9" data-testid="dest-branch-select">
                  <SelectValue placeholder="Select destination branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.filter(b => b.id !== fromBranchId).map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs">Min Margin (₱ / unit)</Label>
              <CalcInput value={minMargin}
 onChange={(v) => setMinMargin(v)}
 className="mt-1 h-9" data-testid="min-margin-input" />
            </div>
            {toBranchId && (
              <Button variant="outline" size="sm" onClick={saveTemplate} className="h-9 mt-5">
                Save as Default
              </Button>
            )}
          </div>

          {/* Category Markup Panel */}
          {toBranchId && (
            <Card style={{ border: "1px solid var(--border-default)" }}>
              <button
                type="button"
                onClick={() => setMarkupPanelOpen(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                data-testid="markup-panel-toggle"
              >
                <span className="flex items-center gap-2">
                  <Settings2 size={15} className="text-[#1A4D2E]" />
                  Category Markup Rules
                  {categoryMarkups.filter(m => parseFloat(m.value) > 0).length > 0 && (
                    <Badge className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E]">
                      {categoryMarkups.filter(m => parseFloat(m.value) > 0).length} active
                    </Badge>
                  )}
                </span>
                {markupPanelOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {markupPanelOpen && (
                <CardContent className="pt-0 pb-4">
                  <p className="text-xs text-slate-400 mb-3">
                    Set per-category add-ons. Applies to all products when added. All values are editable per row.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {categories.map(cat => {
                      const mk = getMarkup(cat);
                      return (
                        <div key={cat} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                          <p className="text-xs font-semibold text-slate-600 mb-2">{cat}</p>
                          <div className="flex gap-1.5">
                            <Select value={mk.type || 'fixed'} onValueChange={v => setMarkup(cat, 'type', v)}>
                              <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {MARKUP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <CalcInput placeholder="0"
 value={mk.value}
 onChange={(v) => setMarkup(cat, 'value', v)}
 className="h-7 text-xs flex-1"
 data-testid={`markup-${cat}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Request context banner */}
          {requestContext && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200">
              <Package size={18} className="text-blue-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-800">Fulfilling Stock Request {requestContext.po_number}</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Review requested quantities vs available stock. Adjust send qty based on what you can fulfill.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setRequestContext(null); cancelEdit(); }} className="text-blue-600 h-7">
                Cancel
              </Button>
            </div>
          )}

          {/* Suggested lines (P7): slow here, selling (or out) there */}
          {toBranchId && !requestContext && suggestions.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/40">
              <button type="button"
                className="w-full flex items-center justify-between px-4 py-2.5"
                onClick={() => setSuggestionsOpen(o => !o)}
                data-testid="transfer-suggestions-toggle">
                <span className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <TrendingUp size={14} />
                  Suggested to send ({suggestions.length})
                  <span className="text-[10px] font-normal text-amber-600">slow here · moving at the destination</span>
                </span>
                {suggestionsOpen ? <ChevronUp size={14} className="text-amber-600" /> : <ChevronDown size={14} className="text-amber-600" />}
              </button>
              {suggestionsOpen && (
                <CardContent className="pt-0 pb-3">
                  <div className="space-y-1.5">
                    {suggestions.map(s => (
                      <div key={s.product_id}
                        className="flex items-center justify-between gap-3 bg-white border border-amber-100 rounded-lg px-3 py-2"
                        data-testid={`suggestion-${s.product_id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-slate-800 truncate">{s.product_name}
                            <span className="text-[10px] text-slate-400 ml-1.5">{s.sku}</span>
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {s.source_qty} on hand · {s.slow_days != null ? `no sale here in ${s.slow_days}d` : 'never sold here'}
                            {s.dest_sold_30d > 0 && <> · destination sold {s.dest_sold_30d} in 30d</>}
                            {s.dest_stock <= 0 && <> · destination is out</>}
                          </p>
                        </div>
                        <Button size="sm" variant="outline"
                          className="h-7 px-2.5 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0"
                          onClick={() => addSuggestion(s)}
                          data-testid={`add-suggestion-${s.product_id}`}>
                          <Plus size={11} className="mr-1" /> Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Product table */}
          {toBranchId && (
            <>
              {(() => { const isFromRequest = !!requestContext; return (
              <Card className="border-slate-200 overflow-visible">
                <CardContent className="p-0">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b-2 border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-3 py-2 text-left" style={{minWidth:'200px'}}>Product</th>
                        {isFromRequest && (
                          <>
                            <th className="px-2 py-2 text-center" style={{minWidth:'70px'}}>
                              Requested
                              <span className="text-[9px] font-normal block text-blue-400">by branch</span>
                            </th>
                            <th className="px-2 py-2 text-center" style={{minWidth:'70px'}}>
                              Available
                              <span className="text-[9px] font-normal block text-slate-400">in stock</span>
                            </th>
                          </>
                        )}
                        <th className="px-2 py-2 text-center" style={{minWidth:'70px'}}>
                          {isFromRequest ? 'Send' : 'Qty'}
                        </th>
                        <th className="px-2 py-2 text-right" style={{minWidth:'100px'}}>Branch Capital</th>
                        <th className="px-2 py-2 text-right" style={{minWidth:'110px'}}>
                          Transfer Capital
                          <span className="text-[9px] font-normal block text-slate-400">(their cost)</span>
                        </th>
                        <th className="px-2 py-2 text-right" style={{minWidth:'110px'}}>
                          Branch Retail
                          <span className="text-[9px] font-normal block text-slate-400">(their sell price)</span>
                        </th>
                        <th className="px-2 py-2 text-center" style={{minWidth:'90px'}}>Margin</th>
                        {isAdmin && <th className="px-2 py-2 text-center" style={{minWidth:'80px'}}>Override</th>}
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const v = row.product ? validateRow(row, minMargin) : { ok: true, margin: 0 };
                        const rowBad = !v.ok && !row.override;
                        return (
                          <React.Fragment key={row.id}>
                          <tr className={`border-b border-slate-100 ${rowBad ? 'bg-red-50/50' : 'hover:bg-slate-50/30'}`}>
                            {/* Product search */}
                            <td className="px-2 py-1.5" style={{minWidth:'200px'}}>
                              {row.product ? (
                                <div>
                                  <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded px-2 h-8">
                                    <span className="text-sm font-medium text-emerald-800 truncate flex-1">{row.product.name}</span>
                                    <button onClick={() => updateRow(row.id, { product: null, productSearch: '', transfer_capital: '', branch_retail: '' })}
                                      className="text-slate-300 hover:text-red-500">
                                      <X size={12} />
                                    </button>
                                  </div>
                                  <p className="text-[10px] text-slate-400 mt-0.5">{row.product.sku} · {row.product.category}</p>
                                </div>
                              ) : (
                                <div className="relative">
                                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                                  <Input value={row.productSearch}
                                    onChange={e => searchProduct(row.id, e.target.value)}
                                    onKeyDown={e => handleRowSearchKeyDown(e, row)}
                                    placeholder="Search or scan barcode\u2026"
                                    className="h-8 pl-7 text-sm"
                                    data-testid={`product-search-${row.id}`}
                                    ref={el => { dropdownRefs.current[row.id] = el; }}
                                    autoComplete="one-time-code" data-lpignore="true"
                                  />
                                  <AnchoredDropdown
                                    anchor={dropdownRefs.current[row.id]}
                                    open={(row.productMatches?.length || 0) > 0}
                                    minWidth={320}
                                  >
                                    {row.productMatches?.map((p, idx) => (
                                      <button key={p.id} onMouseDown={() => selectProduct(row.id, p)}
                                        className={`w-full text-left px-3 py-2 border-b last:border-0 transition-colors ${
                                          idx === row.activeSearchIndex
                                            ? 'bg-emerald-50 border-l-[3px] border-l-emerald-700'
                                            : 'hover:bg-slate-50'
                                        }`}>
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <span className="font-medium text-sm">{p.name}</span>
                                            <span className="text-slate-400 text-xs ml-2">{p.sku} · {p.category}</span>
                                          </div>
                                          <span className="text-xs text-slate-600">{formatPHP(p.branch_capital)}</span>
                                        </div>
                                        {p.last_branch_retail && (
                                          <span className="text-[10px] text-blue-500">Last retail at branch: {formatPHP(p.last_branch_retail)}</span>
                                        )}
                                      </button>
                                    ))}
                                    <div className="px-3 py-1 bg-slate-50 text-[10px] text-slate-400 flex gap-3 border-t sticky bottom-0">
                                      <span>↑↓ navigate</span>
                                      <span>Enter to select</span>
                                      <span>Esc to close</span>
                                    </div>
                                  </AnchoredDropdown>
                                </div>
                              )}
                            </td>

                            {/* Requested Qty — read only, from stock request */}
                            {isFromRequest && (
                              <td className="px-2 py-1.5 text-center">
                                {row.requested_qty != null ? (
                                  <span className="text-sm text-blue-600 font-semibold">{row.requested_qty}</span>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                            )}
                            {/* Available Stock — read only */}
                            {isFromRequest && (
                              <td className="px-2 py-1.5 text-center">
                                {row.available_stock != null ? (
                                  <span className={` text-sm font-semibold ${row.available_stock < (row.requested_qty || 0) ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    {row.available_stock}
                                    {row.available_stock < (row.requested_qty || 0) && (
                                      <span className="block text-[9px] text-amber-500 font-normal">low</span>
                                    )}
                                  </span>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                            )}

                            {/* Qty / Send Qty */}
                            <td className="px-2 py-1.5 text-center">
                              <CalcInput value={row.qty}
 ref={(el) => { qtyRefs.current[row.id] = el; }}
 onChange={(v) => updateRow(row.id, { qty: v })}
 onKeyDown={(e) => {
   // Enter on Qty -> jump to the NEXT row's product search (capital is
   // prefilled by the markup rules) — a 10-line transfer never needs the
   // mouse (audit BT-FLOW-3 / owner fluidity ask).
   if (e.key === 'Enter' && row.product) {
     e.preventDefault();
     const idx = rows.findIndex(r => r.id === row.id);
     const next = rows[idx + 1];
     if (next) {
       setTimeout(() => {
         const el = document.querySelector(`[data-testid="product-search-${next.id}"]`);
         el?.focus?.();
       }, 30);
     }
   }
 }}
 className={`h-8 text-sm text-center  w-16 ${
 isFromRequest && row.requested_qty != null && parseFloat(row.qty) < row.requested_qty
 ? 'border-amber-300 bg-amber-50'
 : ''
 }`}
 data-testid={`qty-${row.id}`} />
                              {isFromRequest && row.requested_qty != null && parseFloat(row.qty) < row.requested_qty && (
                                <span className="text-[9px] text-amber-500 block">partial</span>
                              )}
                            </td>

                            {/* Branch Capital — read-only */}
                            <td className="px-2 py-1.5 text-right">
                              <div className="h-auto flex flex-col items-end justify-center pr-2">
                                <span className="text-sm text-slate-600">{row.product ? formatPHP(row.branch_capital) : '—'}</span>
                                {/* Owner 2026-09-04: MA / LP describe the SENDING branch's own cost
                                    history, so they sit under Branch Capital — reference only. The
                                    Transfer Capital column keeps the receiver-side insights (🎯 / →). */}
                                {row.product && (row.last_purchase_ref != null || row.moving_average_ref != null) && (
                                  <div className="flex justify-end gap-2">
                                    {row.last_purchase_ref != null && <span className="text-[9px] text-slate-400" title="Last purchase at the sending branch">LP: {formatPHP(row.last_purchase_ref)}</span>}
                                    {row.moving_average_ref != null && <span className="text-[9px] text-slate-400" title="Moving average at the sending branch">MA: {formatPHP(row.moving_average_ref)}</span>}
                                  </div>
                                )}
                                {row.product && row.is_branch_specific_cost && (
                                  <span className="text-[9px] text-amber-600 leading-tight">
                                    branch cost (global: {formatPHP(row.global_cost_price)})
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Transfer Capital — editable */}
                            <td className="px-2 py-1.5">
                              <div>
                                <CalcInput
                                  value={row.transfer_capital}
                                  onChange={(v) => updateRow(row.id, { transfer_capital: v })}
                                  onKeyDown={e => {
                                    // Tab on the LAST row when branch_retail is disabled (non-admin) → auto-add new row
                                    if (e.key === 'Tab' && !e.shiftKey && !isAdmin) {
                                      const isLastRow = rows[rows.length - 1]?.id === row.id;
                                      if (isLastRow && row.product) {
                                        e.preventDefault();
                                        const nr = newRow();
                                        setRows(rs => [...rs, nr]);
                                        setTimeout(() => {
                                          const nextInput = document.querySelector(`[data-testid="product-search-${nr.id}"]`);
                                          if (nextInput) nextInput.focus();
                                        }, 50);
                                      }
                                    }
                                  }}
                                  placeholder="0.00"
                                  className={`h-8 text-sm text-right  font-bold ${rowBad && v.reason === 'below_cost' ? 'border-red-400 text-red-700 bg-red-50' : ''}`}
                                  data-testid={`transfer-capital-${row.id}`}
                                  disabled={!row.product}
                                />
                                {row.product && (
                                  <div className="flex flex-col gap-0 mt-0.5">
                                    {toBranchId && (row.target_branch_capital > 0 || row.target_branch_moving_average > 0) && (() => {
                                      const tStock = parseFloat(row.target_branch_stock) || 0;
                                      const sendQty = parseFloat(row.qty) || 0;
                                      const sendCap = parseFloat(row.transfer_capital) || 0;
                                      const newCap = (tStock + sendQty > 0 && sendCap > 0)
                                        ? (tStock * row.target_branch_capital + sendQty * sendCap) / (tStock + sendQty)
                                        : null;
                                      return (
                                        <div className="flex flex-wrap gap-1.5 text-[9px] mt-0.5 pt-0.5 border-t border-slate-100">
                                          <span className="text-purple-600 font-medium" title={`Target branch is currently booking this at ${formatPHP(row.target_branch_capital)}`}>
                                            🎯 {formatPHP(row.target_branch_capital)}
                                          </span>
                                          {row.target_branch_moving_average > 0 && (
                                            <span className="text-purple-400" title="Target branch moving-average">
                                              MA {formatPHP(row.target_branch_moving_average)}
                                            </span>
                                          )}
                                          {newCap != null && (
                                            <span className="text-emerald-600 font-bold" title={`Weighted: (${tStock} × ${formatPHP(row.target_branch_capital)} + ${sendQty} × ${formatPHP(sendCap)}) ÷ ${tStock + sendQty}`}>
                                              → {formatPHP(newCap)}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Branch Retail — admin only for editing */}
                            <td className="px-2 py-1.5">
                              <div>
                                <CalcInput
                                  value={row.branch_retail}
                                  onChange={(v) => updateRow(row.id, { branch_retail: v })}
                                  onKeyDown={e => {
                                    // Tab on the LAST row's last input → auto-add new row & focus it
                                    if (e.key === 'Tab' && !e.shiftKey) {
                                      const isLastRow = rows[rows.length - 1]?.id === row.id;
                                      if (isLastRow && row.product) {
                                        e.preventDefault();
                                        const nr = newRow();
                                        setRows(rs => [...rs, nr]);
                                        setTimeout(() => {
                                          const nextInput = document.querySelector(`[data-testid="product-search-${nr.id}"]`);
                                          if (nextInput) nextInput.focus();
                                        }, 50);
                                      }
                                    }
                                  }}
                                  placeholder="0.00"
                                  className={`h-8 text-sm text-right  font-bold ${rowBad ? 'border-red-400 text-red-700 bg-red-50' : ''} ${!isAdmin ? 'bg-slate-50 text-slate-400' : ''}`}
                                  data-testid={`branch-retail-${row.id}`}
                                  disabled={!row.product || !isAdmin}
                                />
                                {row.last_branch_retail != null && (
                                  <span className="text-[9px] text-blue-400 mt-0.5 block">Last: {formatPHP(row.last_branch_retail)}</span>
                                )}
                                {toBranchId && row.product && row.target_branch_retail > 0 && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <span className="text-[9px] text-purple-600 font-medium" title={`Current retail at ${destBranch?.name || 'target branch'}`}>
                                      🎯 Now: {formatPHP(row.target_branch_retail)}
                                    </span>
                                    {isAdmin && parseFloat(row.branch_retail) !== parseFloat(row.target_branch_retail) && (
                                      <button
                                        type="button"
                                        onClick={() => updateRow(row.id, { branch_retail: String(row.target_branch_retail) })}
                                        className="text-[9px] text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline font-medium"
                                        title="Use the target branch's current retail price"
                                        data-testid={`match-target-retail-${row.id}`}
                                      >match</button>
                                    )}
                                  </div>
                                )}
                                {!isAdmin && row.product && (
                                  <span className="text-[9px] text-slate-400 mt-0.5 block">Admin sets retail</span>
                                )}
                              </div>
                            </td>

                            {/* Margin badge */}
                            <td className="px-2 py-1.5 text-center">
                              {row.product && row.transfer_capital && row.branch_retail ? (
                                <div>
                                  <span className={`text-sm font-bold  ${v.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {v.margin >= 0 ? '+' : ''}{formatPHP(v.margin)}
                                  </span>
                                  {!v.ok && !row.override && (
                                    <p className="text-[9px] text-red-500 leading-tight">
                                      {v.reason === 'below_cost' ? 'Below cost' : `< ₱${minMargin} min`}
                                    </p>
                                  )}
                                </div>
                              ) : <span className="text-slate-300">—</span>}
                            </td>

                            {/* Admin override */}
                            {isAdmin && (
                              <td className="px-2 py-1.5 text-center">
                                {row.product && !v.ok ? (
                                  <div>
                                    <label className="flex items-center gap-1 justify-center cursor-pointer">
                                      <input type="checkbox" checked={row.override}
                                        onChange={e => updateRow(row.id, { override: e.target.checked })}
                                        className="rounded border-slate-300"
                                        data-testid={`override-${row.id}`}
                                      />
                                      <span className="text-[10px] text-amber-600">Override</span>
                                    </label>
                                    {row.override && (
                                      <Input value={row.override_reason}
                                        onChange={e => updateRow(row.id, { override_reason: e.target.value })}
                                        placeholder="Reason" className="h-6 text-[10px] mt-0.5" />
                                    )}
                                  </div>
                                ) : <span className="text-slate-200">—</span>}
                              </td>
                            )}

                            {/* Delete row */}
                            <td className="px-1">
                              {rows.length > 1 && (
                                <button onClick={() => setRows(r => r.filter(x => x.id !== row.id))}
                                  className="p-1 text-slate-300 hover:text-red-500 transition-colors">
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </td>
                          </tr>

                          {/* ── Repack pricing sub-row ── */}
                          {row.product && (row.repacks || []).length > 0 && (
                            <tr className="bg-blue-50/40 border-b border-blue-100">
                              <td colSpan={7 + (isFromRequest ? 2 : 0) + (isAdmin ? 1 : 0)} className="px-3 py-2">
                                <div className="flex items-start gap-1.5 flex-wrap">
                                  <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mr-1 mt-1.5 shrink-0">
                                    📦 Repack prices at destination:
                                  </span>
                                  {(row.repacks || []).map((rp, ri) => {
                                    // Compute new repack capital after this transfer is received
                                    const sendQty = parseFloat(row.qty) || 0;
                                    const sendCap = parseFloat(row.transfer_capital) || 0;
                                    const tStock = parseFloat(row.target_branch_stock) || 0;
                                    const upp = parseFloat(rp.units_per_parent) || 1;
                                    const targetRepackCap = parseFloat(rp.target_capital_per_repack) || 0;
                                    const newParentCap = (tStock + sendQty > 0 && sendCap > 0)
                                      ? (tStock * (row.target_branch_capital || 0) + sendQty * sendCap) / (tStock + sendQty)
                                      : null;
                                    const newRepackCap = newParentCap != null ? newParentCap / upp : null;
                                    return (
                                    <div key={rp.id} className="flex items-center gap-1.5 bg-white border border-blue-200 rounded-lg px-2.5 py-1.5 text-xs">
                                      <div className="text-slate-600 min-w-0">
                                        <span className="font-medium">{rp.name}</span>
                                        <span className="text-slate-400 ml-1">({rp.units_per_parent}/bag)</span>
                                        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                          <span className="text-slate-400" title="Capital per repack at FROM branch">From: {formatPHP(rp.capital_per_repack)}</span>
                                          {targetRepackCap > 0 && (
                                            <span className="text-purple-600 font-medium" title={`Target branch is currently booking each repack at ${formatPHP(targetRepackCap)}`}>
                                              🎯 {formatPHP(targetRepackCap)}
                                            </span>
                                          )}
                                          {newRepackCap != null && (
                                            <span className="text-emerald-600 font-bold" title="New capital per repack after this transfer is received (weighted)">
                                              → {formatPHP(newRepackCap)}
                                            </span>
                                          )}
                                          <span className="text-blue-500" title="Current retail at target branch">Retail: {formatPHP(rp.current_dest_retail)}</span>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1 ml-1">
                                        <span className="text-slate-400">→</span>
                                        <CalcInput value={rp.new_retail_price}
 onChange={(v) => {
 const updated = (row.repacks || []).map((r2, r2i) =>
 r2i === ri ? { ...r2, new_retail_price: v } : r2
 );
 updateRow(row.id, { repacks: updated });
 }}
 placeholder="New price"
className="w-24 h-7 border border-blue-300 rounded-md px-2 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
 data-testid={`repack-price-${rp.id}`} />
                                        {rp.new_retail_price && parseFloat(rp.new_retail_price) > 0 && (
                                          <span className={`text-[10px] font-bold  ml-1 ${parseFloat(rp.new_retail_price) > rp.capital_per_repack ? 'text-emerald-600' : 'text-red-500'}`}>
                                            {parseFloat(rp.new_retail_price) > rp.capital_per_repack
                                              ? `+${formatPHP(parseFloat(rp.new_retail_price) - rp.capital_per_repack)}`
                                              : 'below cost!'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}
                                  <span className="text-[10px] text-slate-400 self-center ml-1">Leave blank to keep current price</span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
              ); })()}

              {/* Add row + Totals + Submit */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRows(r => [...r, newRow()])}
                    data-testid="add-row-btn">
                    <Plus size={14} className="mr-1" /> Add Product
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setParkLabel(''); setParkDialogOpen(true); }}
                    disabled={!rows.some(r => r.product)}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid="park-transfer-btn"
                    title="Save current draft and continue later — like Park Sale">
                    <Pause size={13} className="mr-1" /> {activeParkId ? 'Update Park' : 'Park'}
                  </Button>
                  {parkedTransfers.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => setResumeDialogOpen(true)}
                      className="text-blue-700 border-blue-300 hover:bg-blue-50"
                      data-testid="resume-park-btn">
                      <FolderOpen size={13} className="mr-1" /> Resume <span className="ml-1 text-[10px] bg-blue-600 text-white rounded-full px-1.5">{parkedTransfers.length}</span>
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-6 text-sm">
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Our Cost</p>
                    <p className="font-bold text-slate-700">{formatPHP(totalBranchCapital)}</p>
                  </div>
                  <ArrowRight size={14} className="text-slate-400" />
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Transfer Price</p>
                    <p className="font-bold text-blue-700">{formatPHP(totalTransfer)}</p>
                  </div>
                  <ArrowRight size={14} className="text-slate-400" />
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Branch Retail</p>
                    <p className="font-bold text-emerald-700">{formatPHP(totalRetail)}</p>
                  </div>
                  <Button onClick={editingOrderId ? handleUpdateDraft : () => handleSubmit()}
                    disabled={saving || !toBranchId || !validRows.length}
                    className="bg-[#1A4D2E] hover:bg-[#14532d] text-white ml-4"
                    data-testid="create-transfer-btn">
                    {saving ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                    {editingOrderId ? 'Save Draft' : 'Create Transfer Order'}
                  </Button>
                  {!editingOrderId && !isAdmin && (
                    <Button onClick={() => handleSubmit({ requiresApproval: true })}
                      disabled={saving || !toBranchId || !validRows.length}
                      variant="outline"
                      className="ml-2 border-amber-400 text-amber-700 hover:bg-amber-50"
                      data-testid="submit-for-approval-btn"
                      title="Save the order without retail prices and notify Admin via SMS to approve. Useful when manager doesn't have permission to set retail."
                    >
                      <Shield size={14} className="mr-1.5" /> Submit for Approval
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {!toBranchId && (
            <div className="text-center py-16 text-slate-400">
              <Building2 size={40} className="mx-auto mb-3 opacity-30" />
              <p>{!fromBranchId ? 'Select a source branch first.' : 'Select a destination branch to start building the transfer order.'}</p>
            </div>
          )}
        </TabsContent>


        {/* ── REQUEST STOCK TAB ── */}
        <TabsContent value="request" className="mt-4 space-y-4">
          <Card style={{ border: "1px solid var(--border-default)" }}>
            <CardContent className="p-5 space-y-4">
              <h3 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>Request Stock from Another Branch</h3>
              <p className="text-xs text-slate-500 -mt-2">Send a stock request to another branch. They&apos;ll receive a notification and can generate a transfer.</p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500">Your Branch</label>
                  <Input className="mt-1 h-9 bg-slate-50" value={currentBranch?.name || '—'} disabled />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Request From Branch <span className="text-red-500">*</span></label>
                  <Select value={reqTargetBranch} onValueChange={setReqTargetBranch}>
                    <SelectTrigger className="mt-1 h-9" data-testid="req-target-branch">
                      <SelectValue placeholder="Select branch..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(branches || []).filter(b => b.id !== currentBranch?.id).map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Show retail toggle */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Show retail price suggestion to supply branch:</span>
                <button onClick={() => setReqShowRetail(v => !v)}
                  className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${reqShowRetail ? 'bg-[#1A4D2E]' : 'bg-slate-300'}`}>
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${reqShowRetail ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-[10px] font-medium text-slate-600">{reqShowRetail ? 'ON' : 'OFF'}</span>
              </div>

              {/* Product rows */}
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_100px_60px_40px] gap-2 text-xs font-medium text-slate-500 px-1">
                  <span>Product</span><span>Qty</span><span>Unit</span><span></span>
                </div>
                {reqRows.map((row) => {
                  const targetBranchObj = branches.find(b => b.id === reqTargetBranch);
                  const myBranchId = currentBranch?.id || user?.branch_id || '';
                  return (
                  <div key={row.id} className="grid grid-cols-[1fr_100px_60px_40px] gap-2 items-start" data-testid={`req-row-${row.id}`}>
                    {/* Empty row → SmartProductSearch (Detailed-Sales-style typeahead).
                        Filled row → product summary card with stock context + remove. */}
                    <div className="relative">
                      {!row.product ? (
                        <SmartProductSearch
                          mode="request"
                          branchId={reqTargetBranch || undefined}
                          alsoBranchId={myBranchId || undefined}
                          onSelect={(p) => selectReqProduct(row.id, p)}
                          placeholder={reqTargetBranch ? 'Search product to request...' : 'Pick a supply branch first…'}
                        />
                      ) : (
                        <div className="h-9 px-3 rounded-md border border-emerald-200 bg-emerald-50/40 flex items-center justify-between gap-3"
                             data-testid={`req-product-filled-${row.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate font-medium text-sm">{row.product.name}</span>
                              <span className="text-[10px] text-slate-400 shrink-0">{row.product.sku || ''}</span>
                            </div>
                            <div className="flex gap-3 text-[10px] -mt-0.5">
                              <span className={`font-semibold ${(row.product.available || 0) > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                                {targetBranchObj?.name || 'Supply'}: {fmtQty(row.product.available)} {row.product.unit || ''}
                              </span>
                              {row.product.also_branch_stock !== undefined && (
                                <span className={`font-semibold ${row.product.also_branch_stock > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                                  You: {fmtQty(row.product.also_branch_stock)} {row.product.unit || ''}
                                </span>
                              )}
                            </div>
                          </div>
                          <button type="button"
                            onClick={() => updateReqRow(row.id, { product: null, qty: '' })}
                            className="text-slate-300 hover:text-red-500 transition-colors shrink-0"
                            title="Change product"
                            data-testid={`req-clear-product-${row.id}`}>
                            <X size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                    <CalcInput className="h-9 text-sm text-center"
                      ref={(el) => { reqQtyRefs.current[row.id] = el; }}
                      value={row.qty}
                      onChange={(v) => updateReqRow(row.id, { qty: v })}
                      placeholder="0"
                      disabled={!row.product}
                      data-testid={`req-qty-${row.id}`} />
                    <span className="h-9 flex items-center text-xs text-slate-500 px-1">{row.product?.unit || '—'}</span>
                    <Button variant="ghost" size="sm" tabIndex={-1} className="h-9 w-9 p-0 text-slate-400 hover:text-red-500"
                      onClick={() => removeReqRow(row.id)} disabled={reqRows.length <= 1}>
                      <X size={14} />
                    </Button>
                  </div>
                  );
                })}
                <Button variant="outline" size="sm" className="h-8 text-xs w-full border-dashed" onClick={addReqRow} data-testid="req-add-row">
                  <Plus size={12} className="mr-1" /> Add Product
                </Button>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-medium text-slate-500">Notes (optional)</label>
                <Input className="mt-1 h-9 text-sm" value={reqNotes} onChange={e => setReqNotes(e.target.value)}
                  placeholder="e.g. Urgent — need by Friday" />
              </div>

              {/* Summary & Submit */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="text-sm text-slate-600">
                  <span className="font-medium">{reqRows.filter(r => r.product).length}</span> product(s) ·{' '}
                  <span className="font-medium">{reqRows.filter(r => r.product).reduce((s, r) => s + (parseFloat(r.qty) || 0), 0)}</span> total units
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-10 px-4" onClick={resetReqForm}>Clear</Button>
                  <Button size="sm" className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    onClick={handleSendRequest} disabled={reqSaving} data-testid="send-stock-request-btn">
                    {reqSaving ? <RefreshCw size={14} className="animate-spin mr-2" /> : <ArrowRight size={14} className="mr-2" />}
                    Send Stock Request
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* ── TRANSFERS TAB ── */}
        <TabsContent value="history" className="mt-4 space-y-4">
          {/* Status filter pills */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[
                { key: 'all', label: 'All', dot: 'bg-slate-400' },

                { key: 'pending_approval', label: 'Pending Approval', dot: 'bg-amber-500' },
                { key: 'returned', label: 'Returned', dot: 'bg-rose-500' },
                { key: 'draft', label: 'Drafts', dot: 'bg-slate-400' },
                { key: 'in_transit', label: 'In Transit', dot: 'bg-blue-500' },
                { key: 'checking', label: 'Terminal', dot: 'bg-amber-500' },
                { key: 'pending', label: 'Needs Review', dot: 'bg-orange-500' },
                { key: 'completed', label: 'Completed', dot: 'bg-emerald-500' },
                { key: 'disputes', label: 'Disputes', dot: 'bg-red-500' },
              ].map(st => {
                const count = st.key === 'all' ? orders.length
                  : st.key === 'pending_approval' ? orders.filter(o => o.status === 'pending_approval').length
                  : st.key === 'returned' ? orders.filter(o => o.status === 'returned').length
                  : st.key === 'draft' ? orders.filter(o => o.status === 'draft').length
                  : st.key === 'in_transit' ? orders.filter(o => o.status === 'sent').length
                  : st.key === 'checking' ? orders.filter(o => o.status === 'sent_to_terminal').length
                  : st.key === 'pending' ? orders.filter(o => o.status === 'received_pending').length
                  : st.key === 'completed' ? orders.filter(o => o.status === 'received').length
                  : st.key === 'disputes' ? orders.filter(o => o.status === 'disputed').length
                  : 0;
                const isActive = historyTab === st.key;
                const needsAttention = (st.key === 'pending' || st.key === 'disputes' || st.key === 'pending_approval' || st.key === 'returned') && count > 0;
                return (
                  <button key={st.key}
                    onClick={() => setHistoryTab(st.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-[#1A4D2E] text-white shadow-lg scale-105'
                        : needsAttention
                        ? 'bg-white text-orange-700 border-2 border-orange-400 hover:border-orange-500 shadow-sm'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400 hover:shadow-sm'
                    }`}
                    data-testid={`filter-${st.key}`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${isActive ? 'bg-white' : st.dot}`} />
                    {st.label}
                    {count > 0 && (
                      <span className={`text-xs font-bold min-w-[22px] text-center px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-white/25 text-white' : needsAttention ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'
                      }`}>{count}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <Button variant="outline" size="default" onClick={() => { loadOrders(); loadRequests(); loadOutgoingRequests(); }} disabled={ordersLoading} className="shrink-0 h-10 px-4">
              <RefreshCw size={15} className={`mr-2 ${ordersLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          {/* Content area */}
          {(() => {
            const effectiveBranchId = currentBranch?.id || user?.branch_id || '';

            // (Legacy requests view deleted — P6 trash run. One front door:
            //  /stock-requests. The 'requests' historyTab is unreachable.)

            // ── Transfer Cards ──
            const statusFilter = {
              all: () => true,
              pending_approval: o => o.status === 'pending_approval',
              returned: o => o.status === 'returned',
              draft: o => o.status === 'draft',
              in_transit: o => o.status === 'sent',
              checking: o => o.status === 'sent_to_terminal',
              pending: o => o.status === 'received_pending',
              completed: o => o.status === 'received',
              disputes: o => o.status === 'disputed',
            };
            const filterFn = statusFilter[historyTab] || statusFilter.all;
            // Priority group first, then a date inside the group — rule + reasons
            // live with sortTransfers in lib/transferCard.js (owner 2026-09-19).
            const filteredOrders = sortTransfers(orders.filter(filterFn), o => ({
              isSource: o.from_branch_id === effectiveBranchId,
              isDest: o.to_branch_id === effectiveBranchId,
            }));

            const borderColorMap = {
              draft: 'border-l-slate-300',
              pending_approval: 'border-l-amber-500',
              returned: 'border-l-rose-500',
              sent: 'border-l-blue-500',
              sent_to_terminal: 'border-l-amber-500',
              received_pending: 'border-l-orange-500',
              received: 'border-l-emerald-500',
              disputed: 'border-l-red-500',
              cancelled: 'border-l-red-300',
            };
            const statusLabel = {
              draft: 'Draft',
              pending_approval: 'Pending Approval',
              returned: 'Returned',
              sent: 'In Transit',
              sent_to_terminal: 'On Terminal',
              received_pending: 'Pending Review',
              received: 'Completed',
              disputed: 'Disputed',
              cancelled: 'Cancelled',
            };

            if (ordersLoading) return (
              <div className="text-center py-20 text-slate-400">
                <RefreshCw size={28} className="animate-spin mx-auto mb-4" />
                <p className="text-base">Loading transfers...</p>
              </div>
            );

            if (filteredOrders.length === 0) return (
              <div className="text-center py-20 text-slate-400">
                <Building2 size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-base">No transfers{historyTab !== 'all' ? ` in "${[...Object.entries(statusLabel)].find(([,v]) => v === (statusLabel[historyTab === 'in_transit' ? 'sent' : historyTab]))?.[1] || historyTab}"` : ''} found.</p>
              </div>
            );

            return (
              <div className="space-y-4" data-testid="transfers-list">
                {filteredOrders.map(o => {
                  const fromBr = branches.find(b => b.id === o.from_branch_id);
                  const toBr = branches.find(b => b.id === o.to_branch_id);
                  const isSourceBranch = o.from_branch_id === effectiveBranchId;
                  const isDestBranch = o.to_branch_id === effectiveBranchId;

                  // Card context (owner 2026-09-19): a dated, named timeline for
                  // the path THIS transfer took, and who has to act now. The old
                  // four fixed dots skipped Approval — the very step a "Pending
                  // Approval" card is stuck in — and carried no dates or people.
                  const timelineSteps = buildTimeline(o);
                  const move = whoseMove(o, {
                    isSource: isSourceBranch,
                    isDest: isDestBranch && !isSourceBranch,
                    canApprove: isAdmin || user?.permissions?.branch_transfers?.approve === true,
                  }, { from: fromBr?.name, to: toBr?.name });

                  return (
                    <div key={o.id}
                      className={`bg-white rounded-xl border-2 border-slate-200 border-l-[5px] ${borderColorMap[o.status] || 'border-l-slate-300'} overflow-hidden hover:shadow-xl transition-all duration-200`}
                      data-testid={`transfer-card-${o.id}`}
                    >
                      {/* Header */}
                      <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2.5 flex-wrap">
                            <span className="text-base font-bold text-slate-800 cursor-pointer hover:text-blue-700"
                              onClick={() => { setViewOrder(o); setReceiveDialog(false); }}>{o.order_number}</span>
                            {o.invoice_number && (
                              <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5" data-testid={`invoice-ref-${o.id}`}>
                                {o.invoice_number}
                              </span>
                            )}
                            <Badge className={`text-xs px-2.5 py-0.5 ${STATUS_COLORS[o.status]}`}>
                              {statusLabel[o.status] || o.status}
                            </Badge>
                            {/* Overdue in-transit flag (BT Batch 2 P5, hydration #3):
                                stock sitting in 'sent' for 24h+ is limbo — make it
                                loud on BOTH branches (the SMS reminder already
                                exists; this is the in-app half). */}
                            {/* One age chip for EVERY waiting state — it used to exist
                                only for in-transit ("Overdue — Nd in transit"), so a
                                transfer stuck in approval or variance review for days
                                looked exactly like one created a minute ago. */}
                            <WaitChip since={move?.since} testid={`wait-chip-${o.id}`} />
                            {o.has_shortage && <Badge className="text-xs px-2 py-0.5 bg-red-100 text-red-700">Shortage</Badge>}
                            {(o.source_negative_stock || []).length > 0 && (
                              <Badge className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-200"
                                title="Receiving this transfer pushed the source branch's book stock below zero — count and reconcile."
                                data-testid={`negative-stock-badge-${o.id}`}>
                                <AlertTriangle size={10} className="mr-1" />Source went negative
                              </Badge>
                            )}
                            {o.invoice_creation_failed && !o.invoice_number && (
                              <Badge
                                className="text-xs px-2 py-0.5 bg-rose-100 text-rose-700 border border-rose-200 cursor-help"
                                title={o.invoice_creation_error || 'The internal invoice was not created for this transfer. Please contact admin.'}
                                data-testid={`invoice-failed-${o.id}`}>
                                <AlertTriangle size={10} className="mr-1" />Invoice missing — see admin
                              </Badge>
                            )}
                            {o.incident_ticket_number && (
                              <Badge className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 cursor-pointer hover:bg-amber-200 transition-colors"
                                onClick={(e) => { e.stopPropagation(); navigate('/incident-tickets'); }}
                                data-testid={`ticket-badge-${o.id}`}>
                                <AlertTriangle size={10} className="mr-1" />{o.incident_ticket_number}
                              </Badge>
                            )}
                            {o.terminal_id && o.status === 'received' && (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                                <Smartphone size={11} /> Terminal verified
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-base font-semibold text-slate-700">{fromBr?.name || '?'}</span>
                            <ArrowRight size={16} className="text-slate-400 shrink-0" />
                            <span className="text-base font-semibold text-slate-700">{toBr?.name || '?'}</span>
                            {isSourceBranch && (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5 ml-1" data-testid={`role-sender-${o.id}`}>
                                <Send size={10} /> You: Sender
                              </span>
                            )}
                            {isDestBranch && !isSourceBranch && (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 ml-1" data-testid={`role-receiver-${o.id}`}>
                                <CheckCircle2 size={10} /> You: Receiver
                              </span>
                            )}
                          </div>
                          {o.request_po_number && (
                            <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                              <Package size={12} /> From request {o.request_po_number}
                            </p>
                          )}
                          {o.source_request_id && (
                            <button type="button"
                              onClick={() => navigate(`/stock-requests?view=${o.source_request_id}`)}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1 flex items-center gap-1"
                              data-testid={`from-request-${o.id}`}>
                              <Package size={12} /> From request {o.source_request_number || ''}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Returned reason banner */}
                      {o.status === 'returned' && o.rejection_reason && (
                        <div className="mx-5 mb-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2" data-testid={`rejection-banner-${o.id}`}>
                          <AlertTriangle size={14} className="text-rose-600 mt-0.5 shrink-0" />
                          <div className="text-xs text-rose-700">
                            <span className="font-semibold">Returned by {o.rejected_by_name || 'admin'}:</span>{' '}
                            <span>{o.rejection_reason}</span>
                          </div>
                        </div>
                      )}

                      {/* Whose move — every waiting state, not just pending approval */}
                      <WhoseMoveLine move={move} testid={`whose-move-${o.id}`} />

                      {/* What is in it, in the units the warehouse counts in */}
                      <TransferContents order={o} testid={`contents-${o.id}`} />

                      {/* Dated, named timeline */}
                      <TransferTimeline steps={timelineSteps} testid={`timeline-${o.id}`} />

                      {/* Footer: financials + actions */}
                      <div className="px-5 py-3.5 bg-slate-50/80 border-t border-slate-100 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                        <div className="flex items-center gap-4 text-sm min-w-0">
                          <span className="text-slate-700 font-semibold shrink-0">{formatPHP(o.total_at_transfer_capital)} <span className="text-xs font-normal text-slate-400">capital</span></span>
                          <Separator orientation="vertical" className="h-4" />
                          <span className="text-emerald-600 font-bold shrink-0">{formatPHP(o.total_at_branch_retail)} retail</span>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-1.5 sm:shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => { setViewOrder(o); setReceiveDialog(false); }}
                            className="h-9 px-3 text-slate-500 hover:text-slate-800 text-sm" title="View" data-testid={`view-btn-${o.id}`}>
                            <Eye size={16} className="mr-1" /> View
                          </Button>
                          {['draft', 'sent', 'received', 'received_pending'].includes(o.status) && isSourceBranch && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => printTransferOrder(o, 'thermal')}
                                className="h-9 px-3 text-slate-500 text-xs" title="Print Thermal" data-testid={`print-58mm-btn-${o.id}`}>
                                <Printer size={14} className="mr-1" /> Thermal
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => printTransferOrder(o, 'dot_matrix')}
                                className="h-9 px-3 text-slate-500 text-xs" title="Print Dot Matrix (Epson LX-310)" data-testid={`print-dot-btn-${o.id}`}>
                                <Printer size={14} className="mr-1" /> Dot Matrix
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openSendToPrint('transfer', o)}
                                className="h-9 px-3 text-emerald-700 text-xs" title="Send to a print station or terminal" data-testid={`send-to-print-btn-${o.id}`}>
                                <Send size={14} className="mr-1" /> Remote Print
                              </Button>
                            </>
                          )}
                          {o.status === 'draft' && isSourceBranch && (
                            <Button variant="ghost" size="sm" onClick={() => loadOrderIntoEdit(o)}
                              className="h-9 px-3 text-amber-600 text-sm" title="Edit Draft" data-testid={`edit-btn-${o.id}`}>
                              <Pencil size={16} className="mr-1" /> Edit
                            </Button>
                          )}
                          {o.status === 'draft' && isSourceBranch && (
                            <Button size="sm" onClick={() => handleSend(o.id)}
                              className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" data-testid={`send-btn-${o.id}`}>
                              <Send size={14} className="mr-1.5" /> Send
                            </Button>
                          )}
                          {/* Pending approval — anyone the BACKEND accepts may approve
                              (audit D4: admin OR branch_transfers.approve permission —
                              the old isAdmin gate stranded permission-holding managers
                              whose only working entry was the SMS deep link). */}
                          {o.status === 'pending_approval' && (isAdmin || user?.permissions?.branch_transfers?.approve === true) && (
                            <Button size="sm" onClick={() => navigate(`/approve-transfer/${o.id}`)}
                              className="h-9 px-4 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold" data-testid={`approve-btn-${o.id}`}>
                              <Shield size={14} className="mr-1.5" /> Review & Approve
                            </Button>
                          )}
                          {o.status === 'pending_approval' && !(isAdmin || user?.permissions?.branch_transfers?.approve === true) && (
                            <Badge className="h-9 px-3 bg-amber-50 text-amber-700 border border-amber-200 text-xs flex items-center gap-1.5">
                              <Clock size={12} /> Awaiting Admin
                            </Badge>
                          )}
                          {/* Returned — Manager can edit + resubmit */}
                          {o.status === 'returned' && isSourceBranch && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => loadOrderIntoEdit(o)}
                                className="h-9 px-3 text-amber-600 text-sm" title="Edit & Fix" data-testid={`edit-returned-btn-${o.id}`}>
                                <Pencil size={16} className="mr-1" /> Edit
                              </Button>
                              <Button size="sm" onClick={() => handleResubmit(o.id)}
                                className="h-9 px-4 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold" data-testid={`resubmit-btn-${o.id}`}>
                                <RefreshCw size={14} className="mr-1.5" /> Resubmit
                              </Button>
                            </>
                          )}
                          {o.status === 'sent' && isDestBranch && (
                            <Button size="sm" onClick={() => openReceive(o)}
                              className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid={`receive-btn-${o.id}`}>
                              <CheckCircle2 size={14} className="mr-1.5" /> Receive
                            </Button>
                          )}
                          {o.status === 'sent' && isDestBranch && (
                            <Button variant="ghost" size="sm" onClick={() => sendTransferToTerminal(o.id)}
                              className="h-9 px-3 text-amber-600 text-sm" title="Send to Terminal" data-testid={`send-terminal-btn-${o.id}`}>
                              <Smartphone size={16} className="mr-1" /> Terminal
                            </Button>
                          )}
                          {o.status === 'sent_to_terminal' && (
                            <Badge className="text-xs px-2.5 py-1 bg-amber-100 text-amber-700 flex items-center gap-1.5">
                              <Lock size={12} /> On Terminal
                            </Badge>
                          )}
                          {o.status === 'disputed' && isDestBranch && (
                            <Button size="sm" onClick={() => openReceive(o)}
                              className="h-9 px-4 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold" data-testid={`resubmit-btn-${o.id}`}>
                              <RefreshCw size={14} className="mr-1.5" /> Re-count
                            </Button>
                          )}
                          {o.status === 'received_pending' && isSourceBranch && (
                            <>
                              <Button size="sm" onClick={() => setAcceptDialog(o)}
                                className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid={`accept-btn-${o.id}`}>
                                <CheckCircle2 size={14} className="mr-1.5" /> Accept
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setDisputeDialog(o); setDisputeNote(''); }}
                                className="h-9 px-3 text-red-600 border-red-300 hover:bg-red-50 text-sm font-semibold" data-testid={`dispute-btn-${o.id}`}>
                                <XCircle size={14} className="mr-1.5" /> Dispute
                              </Button>
                            </>
                          )}
                          {/* Owner bug report 2026-08-25: a returned ("bounced
                              back") transfer offered only Edit/Resubmit — it
                              could never be closed, though the backend allows
                              it. Same for withdrawing one still awaiting
                              approval. */}
                          {['draft', 'sent', 'returned', 'pending_approval'].includes(o.status) && isSourceBranch && (
                            <Button variant="ghost" size="sm" onClick={() => handleCancel(o.id)}
                              className="h-9 px-3 text-red-400 hover:text-red-600 text-sm" title="Cancel" data-testid={`cancel-btn-${o.id}`}>
                              <XCircle size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* ── View / Receive Order Dialog ── */}
      <Dialog open={!!viewOrder && !receiveDialog} onOpenChange={() => setViewOrder(null)}
        modal={!btVerifyOpen}>
        {/* Demoted while the PIN verify overlay is up (layer audit
            2026-09-01) — a modal dialog made its PIN un-typeable. */}
        <DialogContent className="max-w-4xl flex flex-col overflow-hidden" style={{ height: '90vh', maxHeight: '90vh' }}
          onInteractOutside={(e) => { if (btVerifyOpen) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (btVerifyOpen) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {viewOrder?.order_number}
              {viewOrder?.invoice_number && (
                <span className="text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 ml-2">{viewOrder.invoice_number}</span>
              )}
              <span className="text-slate-400 mx-1">—</span>
              {branches.find(b => b.id === viewOrder?.from_branch_id)?.name || '?'}
              <span className="text-slate-400 mx-1">→</span>
              {branches.find(b => b.id === viewOrder?.to_branch_id)?.name || '?'}
              <Badge className={`ml-2 text-[10px] ${STATUS_COLORS[viewOrder?.status]}`}>{viewOrder?.status}</Badge>
              {viewOrder?.has_shortage && (
                <Badge className="ml-2 text-[10px] bg-red-100 text-red-700">Shortage</Badge>
              )}
              {viewOrder?.incident_ticket_number && (
                <Badge className="ml-2 text-[10px] bg-amber-100 text-amber-700">{viewOrder.incident_ticket_number}</Badge>
              )}
            </DialogTitle>
            <Button size="sm" variant="outline" className="h-7 text-xs mt-1"
              onClick={() => { setBtUploadOrderId(viewOrder?.id); setBtUploadQROpen(true); }}>
              <Upload size={12} className="mr-1" /> Upload DR / Proof
            </Button>
            <div className="flex gap-2 mt-1">
              <Button size="sm" variant="outline" className="h-7 text-xs bg-slate-800 text-white border-slate-600 hover:bg-slate-700"
                onClick={() => { setBtViewQROpen(true); }}>
                <span className="mr-1">📱</span> View on Phone
              </Button>
              {viewOrder && !viewOrder.verified && (isAdmin || user?.is_auditor) && (
                <Button size="sm" variant="outline" className="h-7 text-xs text-[#1A4D2E] border-[#1A4D2E]/40 hover:bg-[#1A4D2E]/10"
                  onClick={() => { setBtVerifyId(viewOrder?.id); setBtVerifyOpen(true); }}>
                  <CheckCircle2 size={12} className="mr-1" /> Verify
                </Button>
              )}
              {viewOrder?.verified && <VerificationBadge doc={viewOrder} />}
            </div>
          </DialogHeader>

          {/* ── Status Timeline — the SAME component as the list card, so the
              card and this dialog can never tell different stories. ── */}
          {viewOrder && (
            <div className="-mx-5 mb-1 rounded-lg bg-slate-50" data-testid="transfer-timeline">
              <TransferTimeline steps={buildTimeline(viewOrder)} testid="view-timeline" />
            </div>
          )}

          {/* Request reference */}
          {viewOrder?.request_po_id && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs">
              <Package size={13} className="text-blue-600" />
              <span className="text-blue-700">From stock request: <b>{viewOrder.request_po_number}</b></span>
            </div>
          )}
          {viewOrder?.source_request_id && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs">
              <Package size={13} className="text-blue-600" />
              <span className="text-blue-700">From stock request:</span>
              <button type="button"
                onClick={() => navigate(`/stock-requests?view=${viewOrder.source_request_id}`)}
                className="font-bold text-blue-700 hover:text-blue-900 hover:underline"
                data-testid="view-order-from-request">
                {viewOrder.source_request_number || 'Open request'}
              </button>
            </div>
          )}

          {/* Incident ticket link */}
          {viewOrder?.incident_ticket_number && (
            <div className="flex items-center justify-between px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => navigate('/incident-tickets')} data-testid="transfer-ticket-link">
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-600" />
                <span className="text-amber-800">Incident Ticket: <b>{viewOrder.incident_ticket_number}</b></span>
              </div>
              <ArrowRight size={14} className="text-amber-500" />
            </div>
          )}

          {/* ── Dispute / Variance History Timeline ── */}
          {viewOrder && (viewOrder.dispute_note || viewOrder.has_shortage || viewOrder.has_excess || viewOrder.pending_receipt_at) && (() => {
            const events = [];
            // First count (pending receipt)
            if (viewOrder.pending_receipt_at) {
              events.push({
                type: 'counted',
                label: 'First Count Submitted',
                by: viewOrder.pending_receipt_by_name,
                at: viewOrder.pending_receipt_at,
                note: viewOrder.receive_notes,
                icon: 'count',
                shortages: viewOrder.shortages,
                excesses: viewOrder.excesses,
              });
            }
            // Dispute (if any)
            if (viewOrder.dispute_note || viewOrder.disputed_at) {
              events.push({
                type: 'disputed',
                label: 'Disputed by Source',
                by: viewOrder.disputed_by_name,
                at: viewOrder.disputed_at,
                note: viewOrder.dispute_note,
                icon: 'dispute',
              });
            }
            // Re-count (if disputed then re-received)
            if (viewOrder.disputed_at && viewOrder.received_at && viewOrder.received_at > viewOrder.disputed_at) {
              events.push({
                type: 're-counted',
                label: 'Re-count Submitted',
                by: viewOrder.received_by_name || viewOrder.pending_receipt_by_name,
                at: viewOrder.received_at,
                icon: 'recount',
              });
            }
            // Final acceptance
            if (viewOrder.accepted_at) {
              events.push({
                type: 'accepted',
                label: viewOrder.incident_ticket_number ? 'Accepted + Incident Created' : 'Variance Accepted',
                by: viewOrder.accepted_by_name,
                at: viewOrder.accepted_at,
                note: viewOrder.accept_note,
                icon: 'accept',
                ticket: viewOrder.incident_ticket_number,
              });
            }

            if (!events.length) return null;
            return (
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 space-y-0" data-testid="dispute-history">
                <p className="text-[11px] font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                  <Clock size={12} className="text-slate-400" /> Variance History
                </p>
                <div className="relative pl-4 space-y-3">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-300" />
                  {events.map((ev, i) => {
                    const colors = {
                      counted: 'bg-blue-500',
                      dispute: 'bg-red-500',
                      recount: 'bg-amber-500',
                      accept: 'bg-emerald-500',
                    };
                    return (
                      <div key={i} className="relative">
                        <div className={`absolute -left-4 top-0.5 w-2.5 h-2.5 rounded-full ${colors[ev.icon] || 'bg-slate-400'} ring-2 ring-white`} />
                        <div className="ml-2">
                          <p className="text-xs font-semibold text-slate-700">{ev.label}</p>
                          <p className="text-[10px] text-slate-500">
                            {ev.by && <span>{ev.by} · </span>}
                            {fmtDateTime(ev.at)}
                          </p>
                          {ev.note && <p className="text-[10px] text-slate-500 mt-0.5 italic">&quot;{ev.note}&quot;</p>}
                          {ev.ticket && (
                            <button onClick={() => navigate('/incident-tickets')} className="text-[10px] text-amber-700 font-medium mt-0.5 hover:underline flex items-center gap-1">
                              <AlertTriangle size={10} /> {ev.ticket}
                            </button>
                          )}
                          {ev.shortages && ev.shortages.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {ev.shortages.map((s, j) => (
                                <span key={j} className="text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">
                                  {s.product_name}: -{s.variance} {s.unit}
                                </span>
                              ))}
                            </div>
                          )}
                          {ev.excesses && ev.excesses.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {ev.excesses.map((s, j) => (
                                <span key={j} className="text-[10px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">
                                  {s.product_name}: +{Math.abs(s.variance)} {s.unit}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Invoice reference */}
          {viewOrder?.invoice_number && (
            <div className="flex items-center justify-between px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
              <span className="text-emerald-700 flex items-center gap-2">
                <ClipboardCheck size={13} className="text-emerald-600" />
                Invoice: <b>{viewOrder.invoice_number}</b>
                <span className="text-slate-400">|</span>
                Terms: Net 15
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-emerald-700 mr-1">Print:</span>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-700"
                  onClick={() => printTransferOrder(viewOrder, 'dot_matrix')} data-testid="print-invoice-full">
                  Full
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-700"
                  onClick={() => printTransferOrder(viewOrder, 'thermal')} data-testid="print-invoice-58mm">
                  Thermal
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-700"
                  onClick={() => printTransferOrder(viewOrder, 'dot_matrix')} data-testid="print-invoice-dot">
                  Dot Matrix
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-700"
                  onClick={() => openSendToPrint('transfer', viewOrder)} data-testid="send-to-print-invoice">
                  <Send size={11} className="mr-1" /> Remote
                </Button>
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Reconciliation view for received AND received_pending orders */}
            {(viewOrder?.status === 'received' || viewOrder?.status === 'received_pending') ? (
              <div className="space-y-3">
                {viewOrder?.status === 'received_pending' && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-800">
                    <p className="font-semibold flex items-center gap-1.5">
                      <AlertTriangle size={14} /> Pending Your Review
                    </p>
                    <p className="text-xs mt-1">
                      {branches.find(b => b.id === viewOrder?.to_branch_id)?.name || 'Destination'} submitted received quantities with a variance.
                      Review the comparison below and Accept or Dispute.
                    </p>
                    {viewOrder.receive_notes && (
                      <p className="text-xs mt-1 text-amber-600">Receiver's note: "{viewOrder.receive_notes}"</p>
                    )}
                  </div>
                )}
                <div className="text-xs text-slate-500 bg-slate-50 rounded px-3 py-2 flex justify-between">
                  <span>
                    {viewOrder?.status === 'received_pending'
                      ? <>Counted by: <b>{viewOrder.pending_receipt_by_name}</b> · {fmtDateTime(viewOrder.pending_receipt_at)}</>
                      : <>Received by: <b>{viewOrder.received_by_name}</b> · {fmtDate(viewOrder.received_at)}</>
                    }
                  </span>
                  {viewOrder.has_shortage && (
                    <span className="text-red-600 font-medium flex items-center gap-1">
                      <AlertTriangle size={12}/> {viewOrder.shortages?.length} product(s) short
                    </span>
                  )}
                </div>
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white border-b">
                    <tr className="text-[10px] uppercase text-slate-500">
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">Ordered</th>
                      <th className="px-3 py-2 text-right">Received</th>
                      <th className="px-3 py-2 text-right font-semibold">Variance</th>
                      <th className="px-3 py-2 text-right">Capital/unit</th>
                      <th className="px-3 py-2 text-right text-red-600">Capital Loss</th>
                      <th className="px-3 py-2 text-right">Retail/unit</th>
                      <th className="px-3 py-2 text-right text-red-600">Retail Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewOrder?.pending_items || viewOrder?.items || []).map((item, i) => {
                      const qtyOrdered = item.qty_ordered ?? item.qty;
                      const qtyReceived = item.qty_received ?? item.qty;
                      const variance = qtyOrdered - qtyReceived;
                      const capLoss = variance * item.transfer_capital;
                      const retLoss = variance * item.branch_retail;
                      const hasShortage = variance > 0;
                      return (
                        <tr key={i} className={`border-b border-slate-50 ${hasShortage ? 'bg-red-50/40' : 'hover:bg-slate-50'}`}>
                          <td className="px-3 py-2">
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-[10px] text-slate-400">{item.sku} · {item.category}</p>
                          </td>
                          <td className="px-3 py-2 text-right">{qtyOrdered} {item.unit}</td>
                          <td className="px-3 py-2 text-right font-bold">{qtyReceived} {item.unit}</td>
                          <td className={`px-3 py-2 text-right  font-bold ${variance > 0 ? 'text-red-600' : variance < 0 ? 'text-blue-600' : 'text-emerald-600'}`}>
                            {variance === 0 ? '✓ OK' : variance > 0 ? `-${variance}` : `+${Math.abs(variance)}`}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-500">{formatPHP(item.transfer_capital)}</td>
                          <td className={`px-3 py-2 text-right  font-bold ${capLoss > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                            {capLoss > 0 ? `-${formatPHP(capLoss)}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-500">{formatPHP(item.branch_retail)}</td>
                          <td className={`px-3 py-2 text-right  font-bold ${retLoss > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                            {retLoss > 0 ? `-${formatPHP(retLoss)}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    {(() => {
                      const items = viewOrder?.pending_items || viewOrder?.items || [];
                      const totalCapLoss = items.reduce((s,i) => {
                        const v = (i.qty_ordered ?? i.qty) - (i.qty_received ?? i.qty);
                        return s + (v > 0 ? v * i.transfer_capital : 0);
                      }, 0);
                      const totalRetLoss = items.reduce((s,i) => {
                        const v = (i.qty_ordered ?? i.qty) - (i.qty_received ?? i.qty);
                        return s + (v > 0 ? v * i.branch_retail : 0);
                      }, 0);
                      return (
                        <tr className="bg-slate-100 font-bold border-t-2 border-slate-300 text-sm">
                          <td className="px-3 py-2" colSpan={5}>Expected Losses (shortage)</td>
                          <td className="px-3 py-2 text-right text-red-700">{totalCapLoss > 0 ? `-${formatPHP(totalCapLoss)}` : '₱0.00'}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-right text-red-700">{totalRetLoss > 0 ? `-${formatPHP(totalRetLoss)}` : '₱0.00'}</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            ) : (
              // Standard view for draft/sent
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white border-b">
                  <tr className="text-xs uppercase text-slate-500">
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-center">Qty</th>
                    <th className="px-3 py-2 text-right">Branch Capital</th>
                    <th className="px-3 py-2 text-right">Transfer Capital</th>
                    <th className="px-3 py-2 text-right">Branch Retail</th>
                    <th className="px-3 py-2 text-right">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewOrder?.items || []).map((item, i) => {
                    const margin = (item.branch_retail - item.transfer_capital);
                    return (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-xs text-slate-400">{item.sku} · {item.category}</p>
                        </td>
                        <td className="px-3 py-2 text-center">{item.qty} {item.unit}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{formatPHP(item.branch_capital)}</td>
                        <td className="px-3 py-2 text-right font-bold text-blue-700">{formatPHP(item.transfer_capital)}</td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-700">{formatPHP(item.branch_retail)}</td>
                        <td className={`px-3 py-2 text-right  font-bold ${margin >= 20 ? 'text-emerald-600' : 'text-red-500'}`}>
                          +{formatPHP(margin)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {((viewOrder?.status === 'sent' || viewOrder?.status === 'sent_to_terminal') && viewOrder?.to_branch_id === (currentBranch?.id || user?.branch_id || '')) && (
            <div className="pt-3 border-t space-y-3">
              {/* Incoming price updates preview */}
              {(viewOrder?.repack_price_updates || []).filter(rpu => rpu.new_retail_price).length > 0 && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                  <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                    <span>🏷</span> Price Updates on Receive
                  </p>
                  <div className="space-y-1">
                    {(viewOrder.repack_price_updates || []).filter(rpu => rpu.new_retail_price > 0).map((rpu, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-blue-700 font-medium">{rpu.repack_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 line-through">{rpu.current_dest_retail ? `₱${rpu.current_dest_retail}` : '—'}</span>
                          <span className="text-blue-600">→</span>
                          <span className="text-emerald-700 font-bold">₱{rpu.new_retail_price}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-blue-500 mt-1.5">These will be applied when you confirm receipt</p>
                </div>
              )}
              <div className="flex justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-500 mr-1">Print:</span>
                  <Button variant="outline" size="sm" onClick={() => printTransferOrder(viewOrder, 'dot_matrix')} data-testid="print-transfer-btn-full">
                    <Printer size={13} className="mr-1" /> Full
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => printTransferOrder(viewOrder, 'thermal')} data-testid="print-transfer-btn-58mm">
                    <Printer size={13} className="mr-1" /> Thermal
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => printTransferOrder(viewOrder, 'dot_matrix')} data-testid="print-transfer-btn-dot">
                    <Printer size={13} className="mr-1" /> Dot Matrix
                  </Button>
                  <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    onClick={() => openSendToPrint('transfer', viewOrder)} data-testid="send-to-print-transfer-btn">
                    <Send size={13} className="mr-1" /> Remote
                  </Button>
                </div>
                <Button onClick={() => setReceiveDialog(true)} className="bg-emerald-600 text-white">
                  <CheckCircle2 size={15} className="mr-1.5" /> Confirm Receipt
                </Button>
              </div>
            </div>
          )}
          {viewOrder?.status !== 'sent' && (
            <div className="pt-3 border-t flex justify-end items-center gap-1.5">
              <span className="text-xs text-slate-500 mr-1">Print:</span>
              <Button variant="outline" size="sm" onClick={() => printTransferOrder(viewOrder, 'dot_matrix')} data-testid="print-transfer-btn-full-2">
                <Printer size={13} className="mr-1" /> Full
              </Button>
              <Button variant="outline" size="sm" onClick={() => printTransferOrder(viewOrder, 'thermal')} data-testid="print-transfer-btn-58mm-2">
                <Printer size={13} className="mr-1" /> Thermal
              </Button>
              <Button variant="outline" size="sm" onClick={() => printTransferOrder(viewOrder, 'dot_matrix')} data-testid="print-transfer-btn-dot-2">
                <Printer size={13} className="mr-1" /> Dot Matrix
              </Button>
              <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => openSendToPrint('transfer', viewOrder)} data-testid="send-to-print-transfer-btn-2">
                <Send size={13} className="mr-1" /> Remote
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Receive Confirmation Dialog ── */}
      <Dialog open={receiveDialog} onOpenChange={v => { setReceiveDialog(v); if (!v) setViewOrder(null); }}>
        <DialogContent className="max-w-3xl flex flex-col overflow-hidden" style={{ height: '90vh', maxHeight: '90vh' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Confirm Receipt — {viewOrder?.order_number}
              <span className="text-slate-400 text-sm font-normal ml-2">
                from {branches.find(b => b.id === viewOrder?.from_branch_id)?.name || '?'}
              </span>
            </DialogTitle>
          </DialogHeader>

          {/* Wizard step indicator */}
          <div className="flex items-center justify-center gap-1 px-1 pb-1" data-testid="receive-wizard-stepper">
            {[
              { key: 'qty', label: '1. Verify Quantities' },
              { key: 'upload', label: '2. Upload Receipt' },
              { key: 'summary', label: '3. Confirm' },
            ].map((s, i, arr) => {
              const stepIdx = ['qty', 'upload', 'summary'].indexOf(receiveStep);
              const myIdx = ['qty', 'upload', 'summary'].indexOf(s.key);
              const active = myIdx === stepIdx;
              const done = myIdx < stepIdx;
              return (
                <div key={s.key} className="flex items-center">
                  <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    active ? 'bg-emerald-600 text-white shadow-sm' :
                    done ? 'bg-emerald-100 text-emerald-700' :
                    'bg-slate-100 text-slate-400'
                  }`}>
                    {done ? <Check size={12} /> : <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${active ? 'bg-white/20' : 'bg-white/60'}`}>{myIdx + 1}</span>}
                    {s.label.replace(/^\d+\.\s/, '')}
                  </div>
                  {i < arr.length - 1 && <span className={`mx-1 ${myIdx < stepIdx ? 'text-emerald-300' : 'text-slate-200'}`}>—</span>}
                </div>
              );
            })}
          </div>

          {/* ── STEP 1: Verify Quantities ── */}
          {receiveStep === 'qty' && (
          <>
          {/* Explanation banner */}
          <div className="grid grid-cols-2 gap-3 px-1">
            <div className="flex gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <span className="text-amber-600 font-bold text-lg leading-none">↓</span>
              <div>
                <p className="text-xs font-semibold text-amber-800">Shortage (received &lt; ordered)</p>
                <p className="text-[10px] text-amber-700 mt-0.5">Destination gets only what arrived. Missing qty stays in source. Source branch is notified.</p>
              </div>
            </div>
            <div className="flex gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
              <span className="text-blue-600 font-bold text-lg leading-none">↑</span>
              <div>
                <p className="text-xs font-semibold text-blue-800">Excess (received &gt; ordered)</p>
                <p className="text-[10px] text-blue-700 mt-0.5">All extra stock goes to destination, deducted from source. Source branch is alerted to verify their stock.</p>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto mt-1">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b">
                <tr className="text-[10px] uppercase text-slate-500">
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Ordered</th>
                  <th className="px-3 py-2 text-center w-28">Qty Received</th>
                  <th className="px-3 py-2 text-center">Variance</th>
                  <th className="px-3 py-2 text-right">Capital ₱</th>
                  <th className="px-3 py-2 text-right">Impact</th>
                </tr>
              </thead>
              <tbody>
                {(viewOrder?.items || []).map((item, i) => {
                  const ordered = parseFloat(item.qty) || 0;
                  const received = parseFloat(receiveQtys[item.product_id] ?? ordered) || 0;
                  const variance = received - ordered; // positive = excess, negative = shortage
                  const isShort = variance < 0;
                  const isExcess = variance > 0;
                  const isOk = variance === 0;
                  const impact = Math.abs(variance) * item.transfer_capital;
                  const retImpact = Math.abs(variance) * item.branch_retail;

                  return (
                    <tr key={i} className={`border-b ${isShort ? 'bg-amber-50/50' : isExcess ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <p className="text-[10px] text-slate-400">{item.sku} · {item.category}</p>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-500">{ordered} {item.unit}</td>
                      <td className="px-3 py-2">
                        <CalcInput value={receiveQtys[item.product_id] ?? ordered}
 onChange={(v) => setReceiveQtys(q => ({ ...q, [item.product_id]: v }))}
 className={`h-8 text-sm text-center  w-24 mx-auto block font-bold ${
 isShort ? 'border-amber-400 bg-amber-50 text-amber-800' :
 isExcess ? 'border-blue-400 bg-blue-50 text-blue-800' :
 'border-emerald-300'
 }`}
 data-testid={`receive-qty-${item.product_id}`} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isOk && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">✓ OK</span>}
                        {isShort && (
                          <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                            ↓ Short {Math.abs(variance)} {item.unit}
                          </span>
                        )}
                        {isExcess && (
                          <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                            ↑ Excess {variance} {item.unit}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-sm text-blue-700">{formatPHP(item.transfer_capital)}</td>
                      <td className="px-3 py-2 text-right">
                        {isOk && <span className="text-slate-300 text-xs">—</span>}
                        {isShort && (
                          <div className="text-right">
                            <p className="text-[11px] font-bold text-amber-700">-{formatPHP(impact)}</p>
                            <p className="text-[9px] text-amber-500">-{formatPHP(retImpact)} retail</p>
                          </div>
                        )}
                        {isExcess && (
                          <div className="text-right">
                            <p className="text-[11px] font-bold text-blue-700">+{formatPHP(impact)}</p>
                            <p className="text-[9px] text-blue-500">+{formatPHP(retImpact)} retail</p>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Live variance summary */}
            {(() => {
              const items = viewOrder?.items || [];
              let totalShortCapital = 0, totalShortRetail = 0, shortCount = 0;
              let totalExcessCapital = 0, totalExcessRetail = 0, excessCount = 0;
              items.forEach(item => {
                const ordered = parseFloat(item.qty) || 0;
                const received = parseFloat(receiveQtys[item.product_id] ?? ordered) || 0;
                const variance = received - ordered;
                if (variance < 0) {
                  shortCount++;
                  totalShortCapital += Math.abs(variance) * item.transfer_capital;
                  totalShortRetail += Math.abs(variance) * item.branch_retail;
                } else if (variance > 0) {
                  excessCount++;
                  totalExcessCapital += variance * item.transfer_capital;
                  totalExcessRetail += variance * item.branch_retail;
                }
              });
              if (!shortCount && !excessCount) return null;
              return (
                <div className="mt-3 mx-3 space-y-2">
                  {shortCount > 0 && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <div>
                        <p className="text-xs font-bold text-amber-800">Shortage Summary — {shortCount} product(s)</p>
                        <p className="text-[10px] text-amber-600 mt-0.5">Missing quantity stays in source branch. Source branch will be notified.</p>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="text-sm font-bold text-amber-800">-{formatPHP(totalShortCapital)} capital</p>
                        <p className="text-[11px] text-amber-600">-{formatPHP(totalShortRetail)} retail</p>
                      </div>
                    </div>
                  )}
                  {excessCount > 0 && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200">
                      <div>
                        <p className="text-xs font-bold text-blue-800">Excess Summary — {excessCount} product(s)</p>
                        <p className="text-[10px] text-blue-600 mt-0.5">Extra stock will be accepted. Source branch will be alerted to verify inventory.</p>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="text-sm font-bold text-blue-800">+{formatPHP(totalExcessCapital)} capital</p>
                        <p className="text-[11px] text-blue-600">+{formatPHP(totalExcessRetail)} retail</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Notes field */}
            <div className="mx-3 mt-3 mb-2">
              <label className="text-xs text-slate-500 font-medium block mb-1">Receiving Notes (optional)</label>
              <textarea
                value={receiveNotes}
                onChange={e => setReceiveNotes(e.target.value)}
                placeholder="e.g. 2 boxes of ENERTONE arrived damaged, accepted 8 of 10..."
                rows={2}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
                data-testid="receive-notes"
              />
            </div>
          </div>
          </>
          )}

          {/* ── STEP 2: Upload Receipt ── */}
          {receiveStep === 'upload' && (
            <div className="flex-1 min-h-0 overflow-y-auto px-1 py-2 space-y-3" data-testid="receive-step-upload">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Upload size={16} className="text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">Attach a photo of the paper receipt / DR</p>
                    <p className="text-[11px] text-blue-600 mt-0.5">
                      Recommended for audit trail. You can either drag-and-drop a photo from this computer below,
                      or scan the QR code with your phone camera and upload there. The upload is detected automatically.
                    </p>
                  </div>
                </div>
              </div>
              <ReceiptUploadInline
                required={false}
                label="Upload Receipt / DR Photo"
                recordType="branch_transfer"
                recordSummary={viewOrder ? {
                  type_label: 'Branch Transfer',
                  title: viewOrder.order_number || 'Transfer Receipt',
                  description: `${viewOrder.items?.length || 0} item(s)`,
                  amount: viewOrder.total_capital || 0,
                } : undefined}
                onUploaded={(data) => setReceiveReceiptData(data)}
              />
              {receiveReceiptData?.fileCount > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <Check size={16} className="text-emerald-600" />
                  <p className="text-sm text-emerald-800 font-medium">
                    {receiveReceiptData.fileCount} {receiveReceiptData.fileCount === 1 ? 'photo' : 'photos'} uploaded — ready to continue
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Confirm Summary ── */}
          {receiveStep === 'summary' && (() => {
            const items = viewOrder?.items || [];
            const { hasVariance, shortages, excesses } = getVariances(items);
            const totalReceivedCapital = items.reduce((sum, item) => {
              const r = parseFloat(receiveQtys[item.product_id] ?? item.qty) || 0;
              return sum + r * (item.transfer_capital || 0);
            }, 0);
            return (
              <div className="flex-1 min-h-0 overflow-y-auto px-1 py-2 space-y-3" data-testid="receive-step-summary">
                {/* Reference card — for writing on the paper receipt */}
                <div className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white rounded-lg p-4">
                  <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-widest mb-1">Write this on the paper receipt</p>
                  <p className="text-2xl font-black text-slate-800 select-all" data-testid="receive-ref-number">{viewOrder?.order_number}</p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Reference for tracing this delivery in the system if there's a dispute later.
                    From <span className="font-medium">{branches.find(b => b.id === viewOrder?.from_branch_id)?.name}</span> to <span className="font-medium">{branches.find(b => b.id === viewOrder?.to_branch_id)?.name}</span>.
                  </p>
                </div>

                {/* Compact items recap */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 flex justify-between">
                    <span>Receipt summary — {items.length} {items.length === 1 ? 'item' : 'items'}</span>
                    <span className="font-bold text-emerald-700">{formatPHP(totalReceivedCapital)} total capital</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto anchored-dropdown-scroll divide-y divide-slate-100">
                    {items.map((item, i) => {
                      const ordered = parseFloat(item.qty) || 0;
                      const received = parseFloat(receiveQtys[item.product_id] ?? ordered) || 0;
                      const diff = received - ordered;
                      return (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                          <div className="min-w-0 flex-1 truncate">
                            <span className="font-medium">{item.product_name}</span>
                            <span className="text-slate-400 ml-1.5">{item.sku}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-slate-400">{ordered}→</span>
                            <span className={`font-bold ${diff < 0 ? 'text-amber-700' : diff > 0 ? 'text-blue-700' : 'text-emerald-700'}`}>{received}</span>
                            <span className="text-slate-500 w-20 text-right">{formatPHP(received * item.transfer_capital)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Variance final warning */}
                {hasVariance && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs">
                    <p className="font-bold text-amber-800 flex items-center gap-1.5">
                      <AlertTriangle size={12} /> Variance present — {shortages.length} shortage(s), {excesses.length} excess(es)
                    </p>
                    <p className="text-amber-700 mt-1">Source branch will be notified to verify their stock count.</p>
                  </div>
                )}

                {/* Receipt photo confirmation */}
                {receiveReceiptData?.fileCount > 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-xs">
                    <Check size={14} className="text-emerald-600" />
                    <span className="text-emerald-800">{receiveReceiptData.fileCount} receipt photo(s) attached</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 text-xs">
                    <X size={14} className="text-slate-400" />
                    <span className="text-slate-500">No receipt photo attached (optional)</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Footer with step-aware actions ── */}
          <div className="pt-3 border-t flex gap-2 justify-between items-center">
            <p className="text-xs text-slate-400">
              {receiveConfirmStep
                ? 'Quantities differ from the order. Submitting will send to source for confirmation.'
                : receiveStep === 'qty' ? 'Verify each quantity, then continue.'
                : receiveStep === 'upload' ? 'Optional but recommended for audit trail.'
                : 'Final review — write the reference number on the paper receipt before confirming.'}
            </p>
            <div className="flex gap-2">
              {receiveStep === 'qty' && (
                <>
                  <Button variant="outline" onClick={() => { setReceiveDialog(false); setReceiveConfirmStep(false); }}>Cancel</Button>
                  <Button onClick={() => setReceiveStep('upload')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="continue-to-upload-btn">
                    Continue → Upload Receipt
                  </Button>
                </>
              )}
              {receiveStep === 'upload' && (
                <>
                  <Button variant="outline" onClick={() => setReceiveStep('qty')}>← Back</Button>
                  <Button variant="outline" onClick={() => setReceiveStep('summary')}
                    className="text-slate-500"
                    data-testid="skip-receipt-btn">
                    Skip — No Receipt
                  </Button>
                  <Button onClick={() => setReceiveStep('summary')} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="continue-to-summary-btn"
                    disabled={!receiveReceiptData?.fileCount}>
                    Continue → Review
                  </Button>
                </>
              )}
              {receiveStep === 'summary' && (
                <>
                  <Button variant="outline" onClick={() => setReceiveStep('upload')}>← Back</Button>
                  {receiveConfirmStep ? (
                    <Button onClick={handleReceive} disabled={receiveSaving}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="confirm-receive-variance-btn">
                      {receiveSaving ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <AlertTriangle size={14} className="mr-1.5" />}
                      Yes, Submit Variance for Review
                    </Button>
                  ) : (
                    <Button onClick={handleReceive} disabled={receiveSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="confirm-receive-btn">
                      {receiveSaving ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                      Confirm Receipt
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* ── Accept Receipt Dialog — Enhanced with comparison table ── */}
      <Dialog open={!!acceptDialog} onOpenChange={() => setAcceptDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Review Variance — {acceptDialog?.order_number}
            </DialogTitle>
          </DialogHeader>
          {acceptDialog && (
            <div className="space-y-4">
              {/* Comparison Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600">Product</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Sent</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Received</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Variance</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Capital Value</th>
                      <th className="text-right px-3 py-2 font-semibold text-slate-600">Retail Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {[...(acceptDialog.shortages || []), ...(acceptDialog.excesses || [])].map((v, i) => {
                      const isShort = v.variance > 0;
                      return (
                        <tr key={i} className={isShort ? 'bg-red-50/50' : 'bg-blue-50/50'}>
                          <td className="px-3 py-2">
                            <span className="font-medium text-slate-800">{v.product_name}</span>
                            <span className="text-[10px] text-slate-400 ml-1">{v.sku}</span>
                          </td>
                          <td className="px-3 py-2 text-right">{v.qty_ordered} {v.unit}</td>
                          <td className="px-3 py-2 text-right font-bold">{v.qty_received} {v.unit}</td>
                          <td className={`px-3 py-2 text-right  font-bold ${isShort ? 'text-red-600' : 'text-blue-600'}`}>
                            {isShort ? `-${v.variance}` : `+${Math.abs(v.variance)}`}
                          </td>
                          {[['capital_variance', 'transfer_capital'], ['retail_variance', 'branch_retail']].map(([valueKey, unitKey]) => (
                            <td key={valueKey} className="px-3 py-2 text-right">
                              <div className={`font-bold ${isShort ? 'text-red-600' : 'text-blue-600'}`}>
                                {isShort ? '-' : '+'}{formatPHP(v[valueKey] || 0)}
                              </div>
                              <div className="text-[10px] text-slate-400">@ {formatPHP(v[unitKey] || 0)}</div>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {/* Totals — one row per kind that actually exists, valued at capital AND retail */}
                    {varianceTotals(acceptDialog).shortage + varianceTotals(acceptDialog).shortageRetail > 0 && (
                      <tr className="bg-slate-100 font-bold border-t-2" data-testid="variance-total-shortage">
                        <td className="px-3 py-2" colSpan={4}>
                          Short <span className="font-normal text-slate-500">— possible loss, decided on the discrepancy ticket</span>
                        </td>
                        <td className="px-3 py-2 text-right text-red-700">
                          -{formatPHP(varianceTotals(acceptDialog).shortage)}
                        </td>
                        <td className="px-3 py-2 text-right text-red-700">
                          -{formatPHP(varianceTotals(acceptDialog).shortageRetail)}
                        </td>
                      </tr>
                    )}
                    {varianceTotals(acceptDialog).excess + varianceTotals(acceptDialog).excessRetail > 0 && (
                      <tr className="bg-slate-100 font-bold border-t-2" data-testid="variance-total-excess">
                        <td className="px-3 py-2" colSpan={4}>
                          Excess <span className="font-normal text-slate-500">— extra stock added to the books</span>
                        </td>
                        <td className="px-3 py-2 text-right text-blue-700">
                          +{formatPHP(varianceTotals(acceptDialog).excess)}
                        </td>
                        <td className="px-3 py-2 text-right text-blue-700">
                          +{formatPHP(varianceTotals(acceptDialog).excessRetail)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PIN hint — say it BEFORE the click, not after */}
              {varianceTotals(acceptDialog).atRisk > BT_VARIANCE_PIN_THRESHOLD && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="variance-pin-hint">
                  <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                  <span>
                    This variance is worth <b>{formatPHP(varianceTotals(acceptDialog).atRisk)}</b> at capital, above
                    the {formatPHP(BT_VARIANCE_PIN_THRESHOLD)} limit — accepting will ask for a
                    manager PIN, admin PIN, or TOTP next.
                  </span>
                </div>
              )}

              {/* Receiver info */}
              {acceptDialog.pending_receipt_by_name && (
                <div className="text-xs text-slate-500 bg-slate-50 rounded px-3 py-2">
                  Counted by: <b>{acceptDialog.pending_receipt_by_name}</b> · {fmtDateTime(acceptDialog.pending_receipt_at)}
                  {acceptDialog.receive_notes && <span className="ml-2 text-slate-400">Note: "{acceptDialog.receive_notes}"</span>}
                </div>
              )}

              {/* Note input */}
              <div>
                <label className="text-xs text-slate-500 font-medium block mb-1">Note <span className="text-slate-400">(required for investigation)</span></label>
                <textarea
                  placeholder="e.g. Verified with packing list, driver confirmed shortage..."
                  rows={2}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
                  id="accept-note-input" data-testid="accept-note"
                />
              </div>

              {/* 3 Action Buttons */}
              <div className="space-y-2 pt-2 border-t">
                <div className="flex gap-2">
                  <Button onClick={() => {
                    const note = document.getElementById('accept-note-input')?.value || '';
                    handleAcceptReceipt(acceptDialog.id, note, 'accept');
                  }} disabled={actionSaving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-accept-btn">
                    {actionSaving ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                    Accept Variance
                  </Button>
                  <Button onClick={() => {
                    const note = document.getElementById('accept-note-input')?.value || '';
                    if (!note.trim()) { toast.error('Note is required when creating an incident ticket'); return; }
                    handleAcceptReceipt(acceptDialog.id, note, 'accept_with_incident');
                  }} disabled={actionSaving} variant="outline" className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="accept-investigate-btn">
                    {actionSaving ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <AlertTriangle size={14} className="mr-1.5" />}
                    Accept + Investigate
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => {
                    setAcceptDialog(null);
                    setDisputeDialog(acceptDialog);
                    setDisputeNote('');
                  }} className="flex-1 border-red-200 text-red-600 hover:bg-red-50" data-testid="switch-to-dispute-btn">
                    <XCircle size={14} className="mr-1.5" />
                    Dispute & Re-count
                  </Button>
                  <Button variant="ghost" onClick={() => setAcceptDialog(null)} className="text-slate-400">
                    Cancel
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed">
                  <b>Accept Variance</b> — Acknowledge the difference, update inventory, log in audit.
                  <br /><b>Accept + Investigate</b> — Same as accept, but also creates an incident ticket to trace the loss (driver, damage, etc).
                  <br /><b>Dispute & Re-count</b> — Reject the numbers, ask receiver to re-count.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* ── Variance PIN Gate Dialog ── */}
      <Dialog open={!!variancePinDialog} onOpenChange={(v) => { if (!v) { setVariancePinDialog(null); setVariancePin(''); setVariancePinError(''); } }}>
        <DialogContent className="max-w-md" data-testid="variance-pin-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={18} className="text-amber-500" />
              PIN Required — High-Value Variance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <p className="font-semibold mb-1">
                Variance value at capital is above the {formatPHP(BT_VARIANCE_PIN_THRESHOLD)} limit
              </p>
              {variancePinDialog?.totals?.shortage > 0 && (
                <p className="text-xs text-amber-700">
                  Short: <span className="font-bold">{formatPHP(variancePinDialog.totals.shortage)}</span> capital
                  {' · '}<span className="font-bold">{formatPHP(variancePinDialog.totals.shortageRetail)}</span> retail
                </p>
              )}
              {variancePinDialog?.totals?.excess > 0 && (
                <p className="text-xs text-amber-700">
                  Excess: <span className="font-bold">{formatPHP(variancePinDialog.totals.excess)}</span> capital
                  {' · '}<span className="font-bold">{formatPHP(variancePinDialog.totals.excessRetail)}</span> retail
                </p>
              )}
              <p className="text-xs mt-1 text-amber-700">
                A manager PIN, admin PIN, or TOTP is required to accept this variance.
              </p>
            </div>

            <div>
              <label className="text-xs text-slate-600 font-medium block mb-1">
                Manager PIN / Admin PIN / TOTP <span className="text-red-500">*</span>
              </label>
              <input
                type="password" autoComplete="one-time-code" data-lpignore="true"
                value={variancePin}
                onChange={e => { setVariancePin(e.target.value); setVariancePinError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleAcceptWithPin()}
                placeholder="Enter PIN or TOTP code"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 font-mono tracking-widest"
                autoFocus
                data-testid="variance-pin-input"
              />
              {variancePinError && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <XCircle size={12} /> {variancePinError}
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-1 border-t">
              <Button variant="outline" className="flex-1"
                onClick={() => { setVariancePinDialog(null); setVariancePin(''); setVariancePinError(''); }}>
                Cancel
              </Button>
              <Button
                onClick={handleAcceptWithPin}
                disabled={variancePinSaving || !variancePin.trim()}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="variance-pin-submit-btn">
                {variancePinSaving
                  ? <RefreshCw size={14} className="animate-spin mr-1.5" />
                  : <CheckCircle2 size={14} className="mr-1.5" />}
                Accept Variance
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* ── Dispute Receipt Dialog ── */}
      <Dialog open={!!disputeDialog} onOpenChange={() => { setDisputeDialog(null); setDisputeNote(''); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Dispute Receipt — {disputeDialog?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              <p className="font-semibold mb-1">Disputing will:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                <li>NOT update any inventory</li>
                <li>Notify the destination branch to re-count</li>
                <li>Allow them to re-submit corrected quantities</li>
              </ul>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium block mb-1">Reason for dispute <span className="text-red-500">*</span></label>
              <textarea
                value={disputeNote}
                onChange={e => setDisputeNote(e.target.value)}
                placeholder="e.g. Our records show we packed 10 cases, please re-count..."
                rows={3}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                data-testid="dispute-note"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t mt-2">
            <Button variant="outline" onClick={() => { setDisputeDialog(null); setDisputeNote(''); }}>Cancel</Button>
            <Button onClick={handleDisputeReceipt} disabled={actionSaving || !disputeNote.trim()}
              className="bg-red-600 text-white" data-testid="confirm-dispute-btn">
              {actionSaving ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <XCircle size={14} className="mr-1.5" />}
              Send Dispute
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── BRANCH TRANSFER UPLOAD QR ─────────────────────────────────── */}
      <UploadQRDialog
        open={btUploadQROpen}
        onClose={(count) => { setBtUploadQROpen(false); if (count > 0) toast.success(`${count} photo(s) attached to transfer.`); }}
        recordType="branch_transfer"
        recordId={btUploadOrderId}
      />
      <ViewQRDialog
        open={btViewQROpen}
        onClose={() => setBtViewQROpen(false)}
        recordType="branch_transfer"
        recordId={viewOrder?.id}
      />
      <VerifyPinDialog
        open={btVerifyOpen}
        onClose={() => setBtVerifyOpen(false)}
        docType="branch_transfer"
        docId={btVerifyId}
        docLabel={viewOrder?.order_number}
        onVerified={(result) => {
          setBtVerifyOpen(false);
          if (viewOrder) {
            setViewOrder(prev => ({ ...prev, verified: true, verified_by_name: result.verified_by, verification_status: result.status }));
          }
        }}
      />

      {/* Phase 1 — Stock Request Confirmation Layer modal. */}
      <ConfirmQuantitiesDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        request={confirmTargetRequest}
        requestingBranchName={
          (branches.find(b => b.id === confirmTargetRequest?.branch_id) || {}).name || ''
        }
        supplyBranchName={
          (branches.find(b => b.id === confirmTargetRequest?.supply_branch_id) || {}).name || ''
        }
        onConfirmed={() => { setConfirmDialogOpen(false); loadRequests(); }}
      />

      {/* Phase 2.1 — View QR modal (incoming + outgoing request cards). */}
      <RequestQRDialog
        open={qrDialogOpen}
        onOpenChange={setQrDialogOpen}
        request={qrTargetRequest}
        requestingBranchName={
          (branches.find(b => b.id === qrTargetRequest?.branch_id) || {}).name || ''
        }
        supplyBranchName={
          (branches.find(b => b.id === qrTargetRequest?.supply_branch_id) || {}).name || ''
        }
      />

      {/* Smart Capital Pricing Dialog for Branch Transfers */}
      {transferCapitalDialog && transferCapitalPreview && (
        <Dialog open={transferCapitalDialog} onOpenChange={setTransferCapitalDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="price-math-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-800">
                <Check size={18} className="text-[#1A4D2E]" />
                Price Review — Receiving at {transferCapitalPreview.to_branch_name}
              </DialogTitle>
              <DialogDescription className="text-xs">
                The backend computed the capital it will write for <strong>{transferCapitalPreview.order_number}</strong>{' '}
                (higher price → replaces automatically · lower price → cushioned by moving average).
                Confirm each product's retail before finalizing.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {transferCapitalPreview.has_warnings && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
                  <TrendingDown size={14} className="shrink-0 mt-0.5" />
                  <span>Some items arrive <strong>below your current capital</strong> — the moving average cushions those
                  by default. Use the per-line override only if the cheaper price is permanent.</span>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Product</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Current</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Incoming</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">New Avg</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">POS Will Read</th>
                      <th className="text-center px-3 py-2 font-medium text-slate-600 w-24">Retail?</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {transferCapitalPreview.items.map(item => {
                      const override = transferCapitalChoices[item.product_id];
                      const readCapital = override === 'transfer_capital' ? item.transfer_capital
                        : override === 'moving_average' ? (item.simulated_new_ma ?? item.moving_avg)
                        : (item.will_read_capital ?? item.transfer_capital);
                      const methodLabel = override === 'transfer_capital' ? 'new price'
                        : override === 'moving_average' ? 'moving avg'
                        : item.decision === 'moving_average' ? 'moving avg'
                        : item.decision === 'manual' ? 'manual' : 'new price';
                      const retailVal = parseFloat(transferRetailChoices[item.product_id]);
                      const margin = Number.isFinite(retailVal) ? retailVal - readCapital : null;
                      return (
                        <tr key={item.product_id} className={item.needs_warning ? 'bg-amber-50/40' : ''}>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-slate-800 leading-tight">{item.product_name}</div>
                            <div className="text-[10px] text-slate-400">{item.sku} · {item.qty} {item.unit}</div>
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-700">
                            ₱{item.current_dest_capital.toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {item.needs_warning ? (
                              <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
                                <TrendingDown size={12} />₱{item.transfer_capital.toFixed(2)}
                                <span className="text-[10px] text-amber-500">(-{item.price_drop_pct}%)</span>
                              </span>
                            ) : (
                              <span className="text-emerald-700 inline-flex items-center gap-1">
                                <TrendingUp size={12} />₱{item.transfer_capital.toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-500">
                            ₱{(item.simulated_new_ma ?? item.moving_avg).toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="font-bold text-slate-800">₱{Number(readCapital || 0).toFixed(2)}</div>
                            <div className="text-[9px] text-slate-400">{methodLabel}</div>
                            {item.needs_warning && (
                              <div className="flex gap-1 justify-end mt-1">
                                <button
                                  onClick={() => setTransferCapitalChoices(prev => ({ ...prev, [item.product_id]: 'transfer_capital' }))}
                                  className={`px-1.5 py-0.5 rounded text-[9px] font-semibold transition-colors ${
                                    override === 'transfer_capital'
                                      ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-amber-100'
                                  }`} data-testid={`use-transfer-${item.product_id}`}>
                                  New price
                                </button>
                                <button
                                  onClick={() => setTransferCapitalChoices(prev => {
                                    const next = { ...prev }; delete next[item.product_id]; return next;
                                  })}
                                  className={`px-1.5 py-0.5 rounded text-[9px] font-semibold transition-colors ${
                                    !override ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-blue-100'
                                  }`} data-testid={`use-auto-${item.product_id}`}>
                                  Auto
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <CalcInput
                              className="h-8 text-xs text-right"
                              value={transferRetailChoices[item.product_id] ?? ''}
                              onChange={(v) => setTransferRetailChoices(prev => ({ ...prev, [item.product_id]: v }))}
                              placeholder={item.current_dest_retail > 0 ? String(item.current_dest_retail) : '—'}
                              data-testid={`retail-choice-${item.product_id}`}
                            />
                            {margin !== null && (
                              <div className={`text-[9px] text-right mt-0.5 ${margin < 0 ? 'text-rose-600 font-semibold' : 'text-emerald-600'}`}>
                                margin ₱{margin.toFixed(2)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-[11px] text-slate-500">
                Blank retail = keep the branch's current retail untouched. The moving average is stored and shown
                alongside the capital either way.
              </p>

              <div className="flex gap-2 pt-1 border-t">
                <Button variant="outline" className="flex-1" onClick={() => setTransferCapitalDialog(false)}>
                  Back
                </Button>
                <Button onClick={confirmTransferReceive} disabled={receiveSaving}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-price-math-btn">
                  {receiveSaving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <Check size={14} className="mr-1.5" />}
                  Confirm & Receive
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Park Transfer Dialog ── */}
      <Dialog open={parkDialogOpen} onOpenChange={setParkDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="park-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base text-amber-700">
              <Pause size={16} /> Park Transfer
            </DialogTitle>
            <DialogDescription>
              Save this draft and pick it up later. Stock is not reserved while parked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-slate-600">From</span><span className="font-medium">{branches.find(b => b.id === fromBranchId)?.name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">To</span><span className="font-medium">{toBranchId ? (branches.find(b => b.id === toBranchId)?.name || '—') : <span className="text-slate-400">(not yet selected)</span>}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Items in draft</span><span className="font-bold">{rows.filter(r => r.product).length}</span></div>
              <div className="text-[10px] text-amber-600 mt-1">Auto-deletes after 24 hours.</div>
            </div>
            <div>
              <Label className="text-xs">Label (optional)</Label>
              <Input value={parkLabel} onChange={e => setParkLabel(e.target.value)}
                placeholder="e.g. Waiting on truck Tuesday"
                onKeyDown={e => e.key === 'Enter' && handlePark()}
                className="h-9 mt-1" data-testid="park-label-input" />
              <p className="text-[10px] text-slate-400 mt-0.5">Helps you find this park later</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setParkDialogOpen(false)}>Cancel</Button>
              <Button onClick={handlePark} disabled={parkingNow}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="park-confirm-btn">
                {parkingNow ? 'Parking...' : (activeParkId ? 'Update Park' : 'Park Transfer')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Resume Park Dialog ── */}
      <Dialog open={resumeDialogOpen} onOpenChange={setResumeDialogOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="resume-park-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base text-blue-700">
              <FolderOpen size={16} /> Resume Parked Transfer
            </DialogTitle>
            <DialogDescription>
              Pick up where you (or a colleague at this branch) left off. Drafts auto-expire after 24 h.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-1 max-h-96 overflow-y-auto">
            {parkedTransfers.length === 0 && (
              <p className="text-center text-sm text-slate-400 py-8">No parked transfers.</p>
            )}
            {parkedTransfers.map(p => {
              const dest = branches.find(b => b.id === p.to_branch_id);
              const ageMins = Math.round((Date.now() - new Date(p.created_at).getTime()) / 60000);
              const ageStr = ageMins < 60 ? `${ageMins}m ago` : `${Math.floor(ageMins / 60)}h ${ageMins % 60}m ago`;
              return (
                <div key={p.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors flex items-center justify-between gap-3"
                  data-testid={`parked-transfer-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-slate-800 truncate">{p.label || 'Untitled draft'}</span>
                      <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p.item_count} {p.item_count === 1 ? 'item' : 'items'}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><Building2 size={9} className="inline mr-0.5" /> To: <span className="font-medium">{dest?.name || '(none)'}</span></span>
                      <span><Clock size={9} className="inline mr-0.5" /> {ageStr}</span>
                      <span>by {p.created_by_name || 'unknown'}</span>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" onClick={() => handleResume(p.id)}
                      className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`resume-park-${p.id}`}>
                      Resume
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDiscardPark(p.id)}
                      className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`discard-park-${p.id}`}
                      title="Discard this park">
                      <Trash size={13} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark as Closed dialog — works for any non-terminal status */}
      <Dialog open={closePartialDialog} onOpenChange={(v) => { setClosePartialDialog(v); if (!v) setClosePartialReason(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-600" />
              Mark Request as Closed
            </DialogTitle>
            <DialogDescription>
              {closePartialTarget && (() => {
                const s = closePartialTarget.status;
                const label = s === 'partially_fulfilled'
                  ? 'was partially fulfilled. The remaining items will be written off.'
                  : s === 'in_progress'
                    ? `has an active transfer (${closePartialTarget.linked_bto_number || 'BTO'}). Closing will write off any undelivered items.`
                    : s === 'fulfillment_generated'
                      ? 'has a BTO generated but not yet received. Closing marks the request complete as-is.'
                      : 'has not been fulfilled. Closing will write off the entire request.';
                return (
                  <span>
                    Request <strong>{closePartialTarget.po_number}</strong> {label}
                    {' '}Record why you are closing this — auditors will read it.
                  </span>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Reason <span className="text-red-500">*</span>
              </Label>
              <textarea
                value={closePartialReason}
                onChange={(e) => setClosePartialReason(e.target.value)}
                placeholder="e.g. Ordered remaining items directly from supplier; no longer needed from branch."
                rows={3}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="close-partial-reason-input"
                autoFocus
              />
              <p className="text-xs text-slate-400 mt-1">This reason will be recorded for audit purposes.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                'Ordered directly from supplier',
                'No longer needed',
                'Sourced from another branch',
                'Stock not available at supply branch',
                'Customer cancelled remaining order',
              ].map(suggestion => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setClosePartialReason(suggestion)}
                  className="text-xs px-2.5 py-1.5 rounded-full border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-600 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setClosePartialDialog(false)} disabled={closePartialSaving}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleClosePartial}
                disabled={closePartialSaving || !closePartialReason.trim()}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="confirm-close-partial-btn"
              >
                {closePartialSaving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <CheckCircle2 size={13} className="mr-1.5" />}
                Mark as Complete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send to Print Modal — shared by transfer orders + stock requests */}
      {sendToPrintTarget && (
        <SendToPrintModal
          open={sendToPrintOpen}
          onOpenChange={setSendToPrintOpen}
          documentType={sendToPrintTarget.kind === 'request' ? 'purchase_order' : 'branch_transfer'}
          receiverDocType={sendToPrintTarget.kind === 'request' ? 'purchase_order' : 'branch_transfer'}
          documentName={sendToPrintTarget.kind === 'request'
            ? `Stock Request #${sendToPrintTarget.doc.po_number || ''}`
            : `Branch Transfer #${sendToPrintTarget.doc.order_number || ''}`}
          documentId={sendToPrintTarget.doc.id || ''}
          docCode={sendToPrintTarget.doc.doc_code || ''}
          referenceNumber={sendToPrintTarget.kind === 'request'
            ? (sendToPrintTarget.doc.po_number || '')
            : (sendToPrintTarget.doc.order_number || '')}
          branchId={currentBranch?.id || user?.branch_id || ''}
          buildHtml={(format) => sendToPrintTarget.kind === 'request'
            ? getRequestRemoteHtml(sendToPrintTarget.doc, format)
            : getTransferRemoteHtml(sendToPrintTarget.doc, format)}
          metadata={sendToPrintTarget.kind === 'request'
            ? { po_id: sendToPrintTarget.doc.id }
            : { transfer_id: sendToPrintTarget.doc.id }}
        />
      )}

    </div>

  );
}
